(function(){
'use strict';

/* ═══════════════════════════════════════════════════════════════════
   DEADGOOD DESIGN STUDIO — JS
   Engine: proven Moore trace + true polygon offset
   Renderer: canvas-first, animated marching-ants cutline
═══════════════════════════════════════════════════════════════════ */

var MAX_SLOTS = 5;
var CANVAS_RES = 900;   /* internal canvas resolution px */
var CANVAS_PAD = 56;    /* padding inside canvas around sticker */
var DGS_DEBUG = false;  /* production: false = no overlay/labels/console. Set true for dev diagnostics. */

/* ── easily-tunable UI defaults ─────────────────────────────────────
   Corner Roundness (Square/Rectangle only) maps setting 0–5 to a radius factor.
   Setting 0 is a subtle factory corner (never perfectly sharp); 5 is the max. */
var DEFAULT_CORNER = 1;       /* default Corner Roundness setting (0–5) */
var CORNER_MIN = 0.012;       /* radius factor at setting 0 (tiny production corner) */
var CORNER_MAX = 0.35;        /* radius factor at setting 5 (was the old setting-4 look) */
/* Custom-cutline VECTOR QUALITY (see stickerContours). The cutline is produced
   like a professional Offset Path: an EXACT Euclidean offset (constant border
   width — see dilate/erode using edtSqToSeed) whose boundary is then refined to
   sub-pixel positions by projecting each traced node onto the iso-line of a
   lightly blurred coverage field, lightly smoothed and resampled to even spacing.
   No Douglas–Peucker (which re-introduced flats/facets). These affect contour
   QUALITY only — never detection, transparency, the offset radius or border width.
   All work happens inside the cached stickerContours, so Live Preview per-frame
   cost is unchanged. */
var CUT_BLUR_SIGMA      = 1.1;  /* coverage-field blur (mask px) — de-staircases the trace */
var CUT_PROJ_CLAMP      = 2.0;  /* max sub-pixel node move (mask px) — keeps features intact */
var CUT_SMOOTH_PASSES   = 1;    /* light Chaikin pass after projection (was 3 on a raw staircase) */
var CUT_RESAMPLE_SPACING= 1.0;  /* uniform node spacing (mask px) for clean, even curves */
var CUT_MAX_NODES       = 2600; /* per-contour node cap (safety; typical contours stay well under) */
/* Cutline-only analysis resolution. The 512px detection mask stays the single
   source of truth for EVERY decision (transparency, white removal, denoise,
   components, bounds, aspect, DPI); this only re-samples the source image on a
   finer grid so the CONTOUR is built from ~1.5x more edge information. At the
   default 75mm sticker one 512-mask pixel spans ~3 print px in the 1800px export
   — measured staircase flats of 20–160 print px on real exports come straight
   from that quantisation. 768 cuts the grid error by ~1/3 more resolution for
   ~2.25x contour-build cost (build is cached per artwork+border; never per
   frame). Sources smaller than the detection mask gain nothing and skip this. */
var CUT_MASK_RES        = 768;
/* Brand colours reused as the first-time "Custom" colour defaults now come from
   TOK.orange / TOK.yellow (see the design-token block below), so there is no
   second copy of the brand palette to keep in sync. */

/* ── live-preview glossy sheen (on-screen only; never exported) ──────
   A soft diagonal white light sweep clipped to the sticker shape. Future:
   switch the gradient by material type (gloss=white / clear=glass / holo=
   rainbow / matte=none) inside drawSheen(). */
var SHEEN_LIVE  = true;       /* enable the live-preview sheen */
var SHEEN_CYCLE = 4200;       /* ms per diagonal sweep (~16% faster than before) */
var SHEEN_PEAK  = 0.18;       /* main highlight peak white opacity (brighter, still soft) */
var SHEEN_W1    = 0.11;       /* main highlight half-width (crisper core) */
var SHEEN_PEAK2 = 0.05;       /* secondary ambient reflection peak (much lower opacity) */
var SHEEN_W2    = 0.22;       /* secondary band half-width (wider, softer) */
var SHEEN_TRAIL = 0.17;       /* how far the secondary reflection trails behind the main */
var sheenReducedMQL = (function(){try{return window.matchMedia('(prefers-reduced-motion: reduce)');}catch(e){return null;}})();

/* ── Preview Image export only: opportunistically reuse the real DeadGood logo
   (same CDN asset already used by the modals) as the proof-sheet's brand mark,
   instead of drawing plain text. Loaded here with crossOrigin='anonymous' so it
   can never taint the export canvas (confirmed this CDN sends permissive CORS
   headers); buildPreviewSheet() checks .complete before drawing it and falls
   back to a text wordmark if it isn't decoded yet — this can never block, delay
   or break the download, since the check is a synchronous read of .complete. */
/* Preview-sheet brand assets. Both are Shopify-CDN PNGs served with
   Access-Control-Allow-Origin:* — loaded with crossOrigin='anonymous' so
   drawing them can never taint the export canvas (verified: getImageData
   succeeds after drawImage from this origin). Both were inspected: zero
   transparent padding, so sizing from the bitmap bounds is correct.
   Loaded eagerly at startup; buildPreviewSheet checks .complete synchronously
   and falls back to the drawn text treatment, so the sheet never waits and
   never breaks if the CDN is unreachable. */
/* HEADER: wordmark WITHOUT the URL (1231×187, aspect 6.58:1). */
var dgsSheetLogoImg = (function(){
  try{
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = 'https://cdn.shopify.com/s/files/1/0881/3117/4725/files/deadgoostickers-text.png?v=1786183635';
    return img;
  }catch(e){ return null; }
})();
/* FOOTER: the DEADGOODSTICKERS.CO.UK signature (1655×187, aspect 8.85:1) —
   the website version belongs at the foot of the sheet. */
var dgsSheetFooterImg = (function(){
  try{
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = 'https://cdn.shopify.com/s/files/1/0881/3117/4725/files/deadgoostickers-text-logo.png?v=1786181775';
    return img;
  }catch(e){ return null; }
})();

/* ── state ─────────────────────────────────────────────────────── */
var S = {
  artworks: [],            /* Array of artwork objects */
  activeId: null,
  shape: 'custom',
  longestEdgeMm: 75,
  widthMm: 75,
  heightMm: 75,
  sizePreset: '75',
  borderMm: 2.5,
  smoothPasses: 3,         /* custom die-cut contour smoothing — NOT user-facing */
  cornerRadius: 1,         /* Corner Roundness for Square/Rectangle (0–5) */
  borderColour: '#ffffff',
  fillColour: 'none',      /* sticker background behind artwork: 'none' or hex */
  artScale: 1.0,           /* multiplier on artwork draw only */
  customW: 75,             /* custom size width mm (Rectangle custom size only) */
  customH: 75,             /* custom size height mm (Rectangle custom size only) */
  customEdge: 75,          /* custom size LONGEST EDGE mm (Custom/Circle/Square — aspect locked, 40–100) */
  rectW: 100,              /* rectangle default width mm */
  rectH: 60,              /* rectangle default height mm */
  antOffset: 0,            /* for marching ants animation */
  rafId: null
};

var SHAPE_LABELS = { custom:'Custom', circle:'Circle', square:'Square', rectangle:'Rectangle' };
/* Preview-toolbar overrides only. Anything absent here falls through to
   SHAPE_LABELS, which stays the single source of truth for the Design Summary
   and the exported preview sheet. */
var TOOLBAR_SHAPE_LABELS = { custom:'Custom Shape' };

/* ── DOM refs ───────────────────────────────────────────────────── */
var root          = document.querySelector('[data-dgs]');
var fileInput     = root.querySelector('[data-dgs-file]');
var slotsRow      = root.querySelector('[data-dgs-slots]');
var uploadOverlay = root.querySelector('[data-dgs-upload-overlay]');
var canvasEl      = root.querySelector('[data-dgs-canvas]');
var ctx           = canvasEl.getContext('2d');
var stage         = root.querySelector('[data-dgs-stage]');
var dpiBadge      = root.querySelector('[data-dgs-dpi-badge]');
var sizeBadge     = root.querySelector('[data-dgs-size-badge]');
var shapeBadge    = root.querySelector('[data-dgs-shape-badge]');
var borderSlider  = root.querySelector('[data-dgs-border-slider]');
var borderVal     = root.querySelector('[data-dgs-border-val]');
var smoothSlider  = root.querySelector('[data-dgs-smooth-slider]');
var smoothVal     = root.querySelector('[data-dgs-smooth-val]');
var scaleSlider   = root.querySelector('[data-dgs-scale-slider]');
var scaleVal      = root.querySelector('[data-dgs-scale-val]');
var zoomLabel     = root.querySelector('[data-dgs-zoom-label]');
var sumFile       = root.querySelector('[data-dgs-sum-file]');
var sumShape      = root.querySelector('[data-dgs-sum-shape]');
var sumSize       = root.querySelector('[data-dgs-sum-size]');
var sumBorder     = root.querySelector('[data-dgs-sum-border]');
var sumBorderCol  = root.querySelector('[data-dgs-sum-bordercol]');
var sumBg         = root.querySelector('[data-dgs-sum-bg]');
var sumDpi        = root.querySelector('[data-dgs-sum-dpi]');
/* upload count now has TWO instances (topbar + mobile helper row) and is
   written via querySelectorAll in refreshSlots — no single cached node */
var activeName    = root.querySelector('[data-dgs-active-name]');
var stageTip      = root.querySelector('[data-dgs-stage-tip]');
var customSizeWrap = root.querySelector('[data-dgs-custom-size-wrap]');
var customW       = root.querySelector('[data-dgs-custom-w]');
var customH       = root.querySelector('[data-dgs-custom-h]');
var customEdge    = root.querySelector('[data-dgs-custom-edge]');
var customEdgeWrap= root.querySelector('[data-dgs-custom-edge-wrap]');
var customWhWrap  = root.querySelector('[data-dgs-custom-wh-wrap]');
var borderRow     = root.querySelector('[data-dgs-border-row]');
var borderPicker  = root.querySelector('[data-dgs-border-picker]');
var borderHexInput= root.querySelector('[data-dgs-border-hex]');
var fillRow       = root.querySelector('[data-dgs-fill-row]');
var fillPicker    = root.querySelector('[data-dgs-fill-picker]');
var fillHexInput  = root.querySelector('[data-dgs-fill-hex]');

/* ── shared design tokens, resolved for canvas ─────────────────────────────
   The Canvas 2D API cannot read CSS custom properties, so the exports used to
   carry their own hard-coded copy of the palette — which is why the downloaded
   Preview Image drifted from the live UI. These read the SAME --dgs-* tokens the
   stylesheet uses, so there is now ONE source of truth for the whole studio.
   Resolved ONCE here, never per frame: the marching-ants loop calls render() at
   60fps and getComputedStyle in that path would be a real cost.
   The second argument is a fallback used only if a lookup fails, so a missing
   token can never produce an empty fillStyle (which would silently paint black). */
function dgsToken(name,fallback){
  try{
    var v=getComputedStyle(root).getPropertyValue(name);
    return (v&&v.trim())||fallback;
  }catch(e){ return fallback; }
}
var TOK = {
  /* Fallbacks realigned with the CSS tokens in the same cool-neutral pass. They
     fire only if a lookup fails, but a warm fallback would have reintroduced
     exactly the cast this pass removes. The live values still come from the
     stylesheet, so the studio UI and the exported sheet cannot drift apart. */
  bg:      dgsToken('--dgs-bg','#030406'),
  panel:   dgsToken('--dgs-panel-strong','#111317'),
  canvas:  dgsToken('--dgs-canvas','#0d0f13'),
  text:    dgsToken('--dgs-text','#ffffff'),
  muted:   dgsToken('--dgs-text-muted','rgba(255,255,255,.72)'),
  border:  dgsToken('--dgs-border','#34363b'),
  yellow:  dgsToken('--dgs-yellow','#ffd84d'),
  orange:  dgsToken('--dgs-orange','#ff8a1f'),
  success: dgsToken('--dgs-success','#78dfa8'),
  warning: dgsToken('--dgs-warning','#ffd84d'),
  error:   dgsToken('--dgs-error','#ff6b6b'),
  /* Canvas needs a full font stack string, not a CSS variable. Resolved from the
     SAME theme variables the stylesheet uses so the exported sheet speaks the same
     typographic language as the live UI. If a face has not finished loading the
     canvas silently falls back down the stack — same tolerance as the logo. */
  bodyFont: dgsToken('--font-body-family','"Assistant"')+',Helvetica,Arial,sans-serif',
  headFont: dgsToken('--font-heading-family','"Poetsen One"')+',Helvetica,Arial,sans-serif'
};

/* live preview grid mode (false = dark, true = light). Shared by the on-screen toggle
   and the Preview Image export so the proof's preview panel matches what's on screen.
   Session-only; never read by the Print PNG export. */
var gridLight = false;

/* CSS zoom: we scale the canvas display size, not the resolution.
   Pan is a pure viewer offset applied on top of zoom — it never touches the
   sticker geometry, artwork position, or exports. */
var displayZoom = 1.0;
var panX = 0, panY = 0, panning = false, panStartX = 0, panStartY = 0;
/* ── live-preview tilt state — CSS transform only, composed into the SAME canvas
   transform as zoom/pan below. Only ever non-zero at 100%/Fit (zoom≤1); when zoomed
   in, pan owns the transform and tilt stays flat. Never touches render/geometry/exports. */
var tiltRX = 0, tiltRY = 0;            /* applied rotation (deg) */
var tiltTX = 0, tiltTY = 0;            /* drag target (deg) */
var tiltOn = false, overSticker = false;
var tiltStartX = 0, tiltStartY = 0;
var tiltRAF = null, tiltReleaseAt = 0, tiltFromX = 0, tiltFromY = 0, tiltHoverGate = 0;
/* TILT_SENS is literally "pixels of drag to reach full tilt": the mapping is
   k = TILT_MAX/TILT_SENS deg-per-px, so MAX/k === SENS.

   Settled at 7/130, the midpoint of two earlier settings. The original 6/140 was
   too subtle to notice; 8/120 read as slightly too strong in use. 130px-to-max
   sits exactly between 140 and 120, and slope 0.0538 deg/px lands within a
   rounding error of halfway between 0.0429 and 0.0667 — so a 30px drag tilts
   1.62°, against 1.29° originally and 2.00° at the stronger setting.

   PERSPECTIVE / DAMP / RELEASE_MS are deliberately untouched: they own the
   projection strength, follow smoothness and spring-back timing, which were
   already right. Nothing else reads these two constants; the ± clamps in the
   pointermove handler derive from TILT_MAX and scale automatically. */
var TILT_MAX = 7, TILT_PERSPECTIVE = 900, TILT_DAMP = 0.18, TILT_RELEASE_MS = 300, TILT_SENS = 130;
function tiltAvailable(){return !!getActive() && displayZoom<=1.001 && !(sheenReducedMQL&&sheenReducedMQL.matches);}
function updateCanvasCursor(){
  if(displayZoom>1.001){canvasEl.style.cursor=panning?'grabbing':'grab';return;}
  if(tiltOn){canvasEl.style.cursor='grabbing';return;}
  canvasEl.style.cursor=(overSticker&&tiltAvailable())?'grab':'default';
}
function applyCanvasTransform(){
  if(displayZoom>1.001){tiltRX=0;tiltRY=0;}     /* tilt exists ONLY at 100%/Fit */
  canvasEl.style.transform='perspective('+TILT_PERSPECTIVE+'px) rotateX('+tiltRX.toFixed(2)+'deg) rotateY('+tiltRY.toFixed(2)+'deg) translate('+panX+'px,'+panY+'px) scale('+displayZoom+')';
  canvasEl.style.transformOrigin='center center';
  canvasEl.style.touchAction=(displayZoom>1.001||tiltAvailable()||tiltOn)?'none':'auto';
  updateCanvasCursor();
}
function resetView(){
  displayZoom=1;panX=0;panY=0;panning=false;
  tiltOn=false;tiltRX=0;tiltRY=0;tiltTX=0;tiltTY=0;tiltReleaseAt=0;
  if(tiltRAF){cancelAnimationFrame(tiltRAF);tiltRAF=null;}
  if(zoomLabel)zoomLabel.textContent='100%';
  applyCanvasTransform();
}

/* ── artwork-swap fade (Phase 2C) ────────────────────────────────────────────
   The canvas is a persistent element — unlike the slot cards it is never
   recreated — so a CSS animation needs a class toggle to fire. Presentation
   only: it adds/removes one class and touches no state, geometry or rendering.
   OPACITY ONLY by design. canvasEl.style.transform is rewritten by
   applyCanvasTransform() on every zoom/pan/tilt frame, so animating transform
   here would fight that loop. The class is cleared on animationend rather than
   by a timer, so it can never desync from the CSS duration, and re-adding it
   while already present is a no-op rather than a restart. */
function fadeCanvasSwap(){ if(canvasEl) canvasEl.classList.add('dgs-swapping'); }
if(canvasEl) canvasEl.addEventListener('animationend',function(){ canvasEl.classList.remove('dgs-swapping'); });

/* ═══════════════════════════════════════════════════════════════════
   ENGINE — unchanged from proven test harness
═══════════════════════════════════════════════════════════════════ */
function buildAlphaMask(img){
  /* Threshold at the alpha midpoint (128): a real RGBA PNG splits cleanly into
     opaque subject vs transparent background, and thin anti-aliased edge pixels
     are NOT mistaken for stray foreground specks (which break contour tracing). */
  var S2=512,TH=128;
  var sc=Math.min(1,S2/Math.max(img.width,img.height));
  var w=Math.max(4,Math.round(img.width*sc)),h=Math.max(4,Math.round(img.height*sc));
  var oc=document.createElement('canvas');oc.width=w;oc.height=h;
  var ox=oc.getContext('2d',{willReadFrequently:true});
  ox.clearRect(0,0,w,h);ox.drawImage(img,0,0,w,h);
  var d=ox.getImageData(0,0,w,h).data;
  var mask=new Uint8Array(w*h),opaque=0,aMin=255,aMax=0;
  for(var i=0;i<w*h;i++){
    var a=d[i*4+3];
    if(a<aMin)aMin=a;if(a>aMax)aMax=a;
    if(a>=TH){mask[i]=1;opaque++;}
  }
  var transparent=w*h-opaque;
  /* Real alpha channel present (any genuinely transparent pixels exist): ALWAYS
     use the alpha mask. A JPG / flattened PNG has aMin===255 and skips this, so
     real transparent PNGs — even dark art or ones with minimal padding — never
     fall through to white-removal or blob. */
  if(aMin<TH){
    return{mask:mask,w:w,h:h,whiteRemoved:false,opaque:opaque,transparent:transparent,
           alphaMin:aMin,alphaMax:aMax,source:'alpha'};
  }
  /* Background is opaque — try removing a solid light background (white/cream/grey). */
  var wm=buildWhiteRemovalMask(d,w,h);
  if(wm){
    var op2=0;for(var k=0;k<w*h;k++)if(wm[k])op2++;
    return{mask:wm,w:w,h:h,whiteRemoved:true,opaque:op2,transparent:w*h-op2,
           alphaMin:aMin,alphaMax:aMax,source:'white-removal'};
  }
  return{mask:mask,w:w,h:h,whiteRemoved:false,opaque:opaque,transparent:transparent,
         alphaMin:aMin,alphaMax:aMax,source:'opaque'};
}

/* BFS flood-fill from the image border through pixels close in colour to the
   sampled background. Everything the fill cannot reach = foreground artwork.
   Uses colour distance (not just luminance) so it handles white/cream/grey
   backgrounds and will not eat a dark/saturated subject like the cherry. */
function buildWhiteRemovalMask(d,w,h){
  var c0=0,c1=w-1,c2=(h-1)*w,c3=(h-1)*w+w-1;
  var bgR=(d[c0*4]+d[c1*4]+d[c2*4]+d[c3*4])/4;
  var bgG=(d[c0*4+1]+d[c1*4+1]+d[c2*4+1]+d[c3*4+1])/4;
  var bgB=(d[c0*4+2]+d[c1*4+2]+d[c2*4+2]+d[c3*4+2])/4;
  /* Only treat as removable if the background is genuinely light. */
  if((bgR+bgG+bgB)/3<185)return null;
  var TOL=44,TOL2=TOL*TOL;
  function isBg(i){var dr=d[i*4]-bgR,dg=d[i*4+1]-bgG,db=d[i*4+2]-bgB;return dr*dr+dg*dg+db*db<TOL2;}
  var bg=new Uint8Array(w*h),queue=[],qi=0;
  function seed(idx){if(!bg[idx]&&isBg(idx)){bg[idx]=1;queue.push(idx);}}
  for(var x=0;x<w;x++){seed(x);seed((h-1)*w+x);}
  for(var y=1;y<h-1;y++){seed(y*w);seed(y*w+w-1);}
  while(qi<queue.length){
    var cur=queue[qi++],cx=cur%w,cy=(cur/w)|0;
    if(cx>0   &&!bg[cur-1]&&isBg(cur-1)){bg[cur-1]=1;queue.push(cur-1);}
    if(cx<w-1 &&!bg[cur+1]&&isBg(cur+1)){bg[cur+1]=1;queue.push(cur+1);}
    if(cy>0   &&!bg[cur-w]&&isBg(cur-w)){bg[cur-w]=1;queue.push(cur-w);}
    if(cy<h-1 &&!bg[cur+w]&&isBg(cur+w)){bg[cur+w]=1;queue.push(cur+w);}
  }
  var mask=new Uint8Array(w*h),fc=0;
  for(var i=0;i<w*h;i++){if(!bg[i]){mask[i]=1;fc++;}}
  /* Need a real subject, and it must not be the whole frame (fill found nothing). */
  return(fc>20&&fc<w*h*0.97)?mask:null;
}

/* Moore-Neighbor boundary tracing.
   Tracks the backtrack as the actual BACKGROUND cell the trace entered from,
   then sweeps the 8-neighbourhood clockwise starting there. The first
   foreground cell found is the next boundary pixel; the last background cell
   examined becomes the new backtrack. This avoids the previous bug where the
   sweep started on the prior foreground pixel and immediately stepped back. */
function traceContour(mask,w,h){
  function op(x,y){return x>=0&&y>=0&&x<w&&y<h&&mask[y*w+x]===1;}
  /* start = topmost, then leftmost foreground pixel */
  var sx=-1,sy=-1;
  outer:for(var y=0;y<h;y++)for(var x=0;x<w;x++)if(mask[y*w+x]){sx=x;sy=y;break outer;}
  if(sx===-1){
    if(DGS_DEBUG)console.log('[DGS] traceContour FAIL: no foreground pixel in mask');
    return[];
  }

  /* clockwise 8-neighbour offsets (screen space, y increases downward):
     0:E 1:SE 2:S 3:SW 4:W 5:NW 6:N 7:NE */
  var DX=[1,1,0,-1,-1,-1,0,1], DY=[0,1,1,1,0,-1,-1,-1];
  function dirIndex(dx,dy){for(var k=0;k<8;k++)if(DX[k]===dx&&DY[k]===dy)return k;return -1;}

  var pts=[{x:sx,y:sy}];
  var cx=sx,cy=sy;
  /* the cell west of start is background (start is leftmost on its top row) */
  var bx=sx-1,by=sy;
  var maxSteps=w*h*4+64,steps=0,closed=false,failReason='';

  while(true){
    var d0=dirIndex(bx-cx,by-cy); if(d0<0)d0=4;
    var found=-1,prevX=bx,prevY=by;
    for(var i=0;i<8;i++){
      var dir=(d0+i)%8,nx=cx+DX[dir],ny=cy+DY[dir];
      if(op(nx,ny)){found=dir;break;}
      prevX=nx;prevY=ny; /* last background cell examined */
    }
    if(found<0){failReason='isolated start pixel (no foreground neighbour)';break;}
    bx=prevX;by=prevY;                 /* new backtrack = last background cell */
    cx=cx+DX[found];cy=cy+DY[found];   /* step onto the new boundary pixel */
    pts.push({x:cx,y:cy});
    if(++steps>maxSteps){failReason='exceeded max steps ('+maxSteps+')';break;}
    if(cx===sx&&cy===sy){closed=true;break;}
  }

  if(DGS_DEBUG){
    console.log('[DGS] traceContour',{
      startX:sx,startY:sy,
      rawPointCount:pts.length,
      contourClosed:closed,
      first10Points:pts.slice(0,10),
      failReason:failReason||'(none — traced OK)'
    });
  }
  return pts;
}

/* Bounding box of ALL set pixels in a mask (null if empty). */
function maskBounds(mask,w,h){
  var mnx=1e9,mny=1e9,mxx=-1,mxy=-1;
  for(var y=0;y<h;y++)for(var x=0;x<w;x++){
    if(mask[y*w+x]){if(x<mnx)mnx=x;if(x>mxx)mxx=x;if(y<mny)mny=y;if(y>mxy)mxy=y;}
  }
  if(mxx<0)return null;
  return{minX:mnx,minY:mny,maxX:mxx,maxY:mxy,w:mxx-mnx+1,h:mxy-mny+1};
}

/* Remove ONLY genuine specks: 8-connected components smaller than minArea px.
   Keeps every intentional detail (dots, thin lines, small text, chains). */
function denoiseMask(mask,w,h,minArea){
  var lbl=new Int32Array(w*h),counts=[0],cur=0,stack=[];
  for(var s=0;s<w*h;s++){
    if(mask[s]&&!lbl[s]){
      cur++;counts[cur]=0;stack.length=0;stack.push(s);lbl[s]=cur;
      while(stack.length){
        var p=stack.pop();counts[cur]++;
        var px=p%w,py=(p/w)|0;
        for(var dy=-1;dy<=1;dy++)for(var dx=-1;dx<=1;dx++){
          if(!dx&&!dy)continue;var nx=px+dx,ny=py+dy;
          if(nx<0||ny<0||nx>=w||ny>=h)continue;var np=ny*w+nx;
          if(mask[np]&&!lbl[np]){lbl[np]=cur;stack.push(np);}
        }
      }
    }
  }
  var out=new Uint8Array(w*h),kept=0,comps=0;
  for(var c=1;c<=cur;c++)if(counts[c]>=minArea)comps++;
  for(var i=0;i<w*h;i++){var id=lbl[i];if(id&&counts[id]>=minArea){out[i]=1;kept++;}}
  return{mask:out,kept:kept,components:comps,total:cur};
}

/* Moore-Neighbor trace of a single labelled component (op = lbl===id). */
function traceLabelContour(lbl,id,w,h,startIdx){
  function op(x,y){return x>=0&&y>=0&&x<w&&y<h&&lbl[y*w+x]===id;}
  var sx=startIdx%w,sy=(startIdx/w)|0;
  var DX=[1,1,0,-1,-1,-1,0,1],DY=[0,1,1,1,0,-1,-1,-1];
  function dirIndex(dx,dy){for(var k=0;k<8;k++)if(DX[k]===dx&&DY[k]===dy)return k;return -1;}
  var pts=[{x:sx,y:sy}],cx=sx,cy=sy,bx=sx-1,by=sy,maxSteps=w*h*4+64,steps=0;
  while(true){
    var d0=dirIndex(bx-cx,by-cy);if(d0<0)d0=4;
    var found=-1,prevX=bx,prevY=by;
    for(var i=0;i<8;i++){var dir=(d0+i)%8,nx=cx+DX[dir],ny=cy+DY[dir];if(op(nx,ny)){found=dir;break;}prevX=nx;prevY=ny;}
    if(found<0)break;
    bx=prevX;by=prevY;cx+=DX[found];cy+=DY[found];
    pts.push({x:cx,y:cy});
    if(++steps>maxSteps)break;
    if(cx===sx&&cy===sy)break;
  }
  return pts;
}

/* Trace the outer boundary of EVERY 8-connected component (>= minArea). This is
   the key to including all artwork islands, not just the largest one. */
function traceAllContours(mask,w,h,minArea){
  var lbl=new Int32Array(w*h),cur=0,stack=[],comps=[];
  for(var s=0;s<w*h;s++){
    if(mask[s]&&!lbl[s]){
      cur++;var cnt=0,start=s;stack.length=0;stack.push(s);lbl[s]=cur;
      while(stack.length){
        var p=stack.pop();cnt++;
        var px=p%w,py=(p/w)|0;
        for(var dy=-1;dy<=1;dy++)for(var dx=-1;dx<=1;dx++){
          if(!dx&&!dy)continue;var nx=px+dx,ny=py+dy;
          if(nx<0||ny<0||nx>=w||ny>=h)continue;var np=ny*w+nx;
          if(mask[np]&&!lbl[np]){lbl[np]=cur;stack.push(np);}
        }
      }
      comps.push({id:cur,count:cnt,start:start});
    }
  }
  var contours=[];
  for(var ci=0;ci<comps.length;ci++){
    var co=comps[ci];if(co.count<minArea)continue;
    var ct=traceLabelContour(lbl,co.id,w,h,co.start);
    if(ct.length>=4)contours.push(ct);
  }
  return contours;
}

function subsample(pts,target){
  if(pts.length<=target)return pts.slice();
  var out=[],step=pts.length/target;
  for(var i=0;i<target;i++)out.push(pts[Math.round(i*step)%pts.length]);
  return out;
}

function chaikin(pts,passes){
  var cur=pts.slice();
  for(var p=0;p<passes;p++){
    var next=[];
    for(var i=0;i<cur.length;i++){
      var a=cur[i],b=cur[(i+1)%cur.length];
      next.push({x:.75*a.x+.25*b.x,y:.75*a.y+.25*b.y});
      next.push({x:.25*a.x+.75*b.x,y:.25*a.y+.75*b.y});
    }
    cur=next;
  }
  return cur;
}

/* ── Sub-pixel vector refinement (professional Offset-Path quality) ──────────
   These turn a raster boundary trace into a smooth, sub-pixel, constant-width
   vector contour. They run ONCE per cutline build (inside the cached
   stickerContours), never per animation frame. */

/* Exact Euclidean squared distance transform (Felzenszwalb–Huttenlocher, O(n)).
   seed[i]!=0 marks a zero-distance pixel; returns squared distance to the
   nearest seed for every pixel. This replaces the old (1,√2) chamfer metric,
   whose ~7.6% anisotropy made the offset (and therefore the border) wider on
   diagonals than on axes. Exact EDT ⇒ a mathematically constant offset. */
function edtSqToSeed(seed,w,h){
  var INF=1e20,n=w*h,f=new Float64Array(n),i,x,y;
  for(i=0;i<n;i++)f[i]=seed[i]?0:INF;
  var len=Math.max(w,h),d=new Float64Array(len),v=new Int32Array(len),z=new Float64Array(len+1),col=new Float64Array(h);
  function edt1d(g,m,out){
    var k=0;v[0]=0;z[0]=-INF;z[1]=INF;
    for(var q=1;q<m;q++){
      var s=((g[q]+q*q)-(g[v[k]]+v[k]*v[k]))/(2*q-2*v[k]);
      while(s<=z[k]){k--;s=((g[q]+q*q)-(g[v[k]]+v[k]*v[k]))/(2*q-2*v[k]);}
      k++;v[k]=q;z[k]=s;z[k+1]=INF;
    }
    k=0;
    for(var q2=0;q2<m;q2++){
      while(z[k+1]<q2)k++;
      out[q2]=(q2-v[k])*(q2-v[k])+g[v[k]];
    }
  }
  /* transform along columns, then along rows */
  for(x=0;x<w;x++){
    for(y=0;y<h;y++)col[y]=f[y*w+x];
    edt1d(col,h,d);
    for(y=0;y<h;y++)f[y*w+x]=d[y];
  }
  var row=new Float64Array(w);
  for(y=0;y<h;y++){
    for(x=0;x<w;x++)row[x]=f[y*w+x];
    edt1d(row,w,d);
    for(x=0;x<w;x++)f[y*w+x]=d[x];
  }
  return f;
}

/* Separable Gaussian blur of a binary region → continuous coverage field in
   [0,1]. Its 0.5 iso-line is the smooth, sub-pixel silhouette of the region;
   projecting the staircase trace onto it removes the 1px pixel steps without the
   axis-aligned artefacts a raw distance field leaves at cardinal tangents. */
function gaussBlurField(region,w,h,sigma){
  var rad=Math.max(1,Math.ceil(sigma*2.5)),i,t,ker=new Float64Array(rad*2+1),sum=0;
  for(t=-rad;t<=rad;t++){var e=Math.exp(-(t*t)/(2*sigma*sigma));ker[t+rad]=e;sum+=e;}
  for(i=0;i<ker.length;i++)ker[i]/=sum;
  var n=w*h,tmp=new Float32Array(n),out=new Float32Array(n),x,y,acc,xx,yy;
  for(y=0;y<h;y++)for(x=0;x<w;x++){
    acc=0;for(t=-rad;t<=rad;t++){xx=x+t;if(xx<0)xx=0;else if(xx>=w)xx=w-1;acc+=region[y*w+xx]*ker[t+rad];}
    tmp[y*w+x]=acc;
  }
  for(y=0;y<h;y++)for(x=0;x<w;x++){
    acc=0;for(t=-rad;t<=rad;t++){yy=y+t;if(yy<0)yy=0;else if(yy>=h)yy=h-1;acc+=tmp[yy*w+x]*ker[t+rad];}
    out[y*w+x]=acc;
  }
  return out;
}

/* Project each integer trace vertex onto the `level` iso-line of a smooth field
   with one bilinear Newton step (move along the gradient onto the iso). The move
   is clamped to `clamp` px so a node can only be nudged onto the true edge, never
   relocated — features traced from the binary mask are always preserved. */
function projectToIso(loop,field,w,h,level,clamp){
  function samp(px,py){
    if(px<0)px=0;else if(px>w-1.001)px=w-1.001;
    if(py<0)py=0;else if(py>h-1.001)py=h-1.001;
    var x0=px|0,y0=py|0,fx=px-x0,fy=py-y0,o=y0*w+x0;
    var a=field[o],b=field[o+1],c=field[o+w],d=field[o+w+1];
    return (a*(1-fx)+b*fx)*(1-fy)+(c*(1-fx)+d*fx)*fy;
  }
  var out=[],clamp2=clamp;
  for(var k=0;k<loop.length;k++){
    var x=loop[k].x,y=loop[k].y;
    var c0=samp(x,y),gx=samp(x+0.5,y)-samp(x-0.5,y),gy=samp(x,y+0.5)-samp(x,y-0.5);
    var g2=gx*gx+gy*gy;
    if(g2<1e-9){out.push({x:x,y:y});continue;}
    var stp=(level-c0)/g2,dx=gx*stp,dy=gy*stp,m=Math.sqrt(dx*dx+dy*dy);
    if(m>clamp2){dx*=clamp2/m;dy*=clamp2/m;}
    out.push({x:x+dx,y:y+dy});
  }
  return out;
}

/* Uniform arc-length resample of a CLOSED polyline to even node spacing (clean,
   intentional, professional curve), capped at maxN nodes for safety. */
function resampleClosed(pts,spacing,maxN){
  var n=pts.length;if(n<3)return pts.slice();
  var seg=new Float64Array(n),total=0,i;
  for(i=0;i<n;i++){var a=pts[i],b=pts[(i+1)%n],dx=b.x-a.x,dy=b.y-a.y;seg[i]=Math.sqrt(dx*dx+dy*dy);total+=seg[i];}
  if(total<1e-6)return pts.slice();
  var m=Math.max(8,Math.min(maxN,Math.round(total/spacing))),out=[],step=total/m,acc=0,target=0,idx=0;
  for(var k=0;k<m;k++){
    while(idx<n-1&&acc+seg[idx]<target){acc+=seg[idx];idx++;}
    var a2=pts[idx],b2=pts[(idx+1)%n],t=seg[idx]>1e-9?(target-acc)/seg[idx]:0;
    out.push({x:a2.x+(b2.x-a2.x)*t,y:a2.y+(b2.y-a2.y)*t});
    target+=step;
  }
  return out;
}

/* True constant-distance outward offset with ROUND joins.
   Every output point is placed exactly `dist` along an outward edge normal, and
   convex corners are bridged by a radius-`dist` arc. No point is ever pulled
   back toward the contour, so the border width stays uniform on tight curves
   (stem, leaves, fingers, tread) and the cutline can never bite into the art.
   Input is assumed oriented so that (dy,-dx) is the outward normal — the same
   convention the previous offset used and the renderer relies on. */
function offsetPolygon(pts,dist){
  var n=pts.length;
  if(n<3||dist===0)return pts.slice();
  /* outward unit normal + unit direction per edge i (pts[i] -> pts[i+1]) */
  var E=[];
  for(var i=0;i<n;i++){
    var a=pts[i],b=pts[(i+1)%n],dx=b.x-a.x,dy=b.y-a.y,len=Math.sqrt(dx*dx+dy*dy)||1e-6;
    E.push({nx:dy/len,ny:-dx/len,dx:dx/len,dy:dy/len});
  }
  var out=[],ARC=0.40; /* radians between arc samples (~23°) */
  for(var j=0;j<n;j++){
    var p=pts[j];
    var e1=E[(j-1+n)%n];           /* edge arriving at p */
    var e2=E[j];                   /* edge leaving p   */
    var pIn ={x:p.x+e1.nx*dist,y:p.y+e1.ny*dist};   /* p offset by incoming normal */
    var pOut={x:p.x+e2.nx*dist,y:p.y+e2.ny*dist};   /* p offset by outgoing normal */
    var cross=e1.dx*e2.dy-e1.dy*e2.dx;              /* >0 convex, <0 concave */
    if(cross>1e-6){
      /* convex: sweep a radius-dist arc around p from pIn to pOut (outward) */
      var a0=Math.atan2(pIn.y-p.y,pIn.x-p.x);
      var a1=Math.atan2(pOut.y-p.y,pOut.x-p.x);
      var da=a1-a0;while(da<=0)da+=Math.PI*2;       /* short outward sweep, (0,2π) */
      var steps=Math.max(1,Math.round(da/ARC));
      for(var s=0;s<=steps;s++){
        var ang=a0+da*(s/steps);
        out.push({x:p.x+Math.cos(ang)*dist,y:p.y+Math.sin(ang)*dist});
      }
    } else {
      /* concave / straight: both offset points are exactly `dist` outward;
         the small overlap is resolved by the fill winding. Never inward. */
      out.push(pIn);
      out.push(pOut);
    }
  }
  return out;
}

/* ── Mask-based outward offset (die-cut border) ───────────────────────────
   Grow the binary artwork mask outward by the border radius, then trace the
   OUTER boundary of the grown region. That boundary is always one simple,
   closed curve, so it can never self-intersect and has no internal segments —
   the cutline can never appear inside the sticker. Narrow gaps are bridged
   when the border is wider than the gap, exactly like a real die-cut. */
function dilateMaskByRadius(mask,w,h,r){
  /* EXACT Euclidean dilation: grow to every pixel within true distance r of the
     foreground. (Was a (1,√2) chamfer transform whose anisotropy varied the
     offset — and therefore the border — by direction.) */
  var sq=edtSqToSeed(mask,w,h),n=w*h,out=new Uint8Array(n),r2=r*r;
  for(var i=0;i<n;i++)if(sq[i]<=r2)out[i]=1;
  return out;
}

/* Inverse of dilate: shrink the mask by `r` px using the EXACT distance from the
   foreground to the nearest background; keep pixels farther than r. Paired with
   dilate to form a morphological CLOSING. */
function erodeMaskByRadius(mask,w,h,r){
  var n=w*h,inv=new Uint8Array(n),i;
  for(i=0;i<n;i++)inv[i]=mask[i]?0:1;          /* seeds = background */
  var sq=edtSqToSeed(inv,w,h),out=new Uint8Array(n),r2=r*r;
  for(i=0;i<n;i++)if(mask[i]&&sq[i]>r2)out[i]=1;
  return out;
}

/* Outer contours (MASK coords) of the FULL artwork mask grown by `rMask` px.
   Traces EVERY component, so all islands (text, dots, chains, separate letters)
   are included — not just the largest blob. Returns an array of contours.
   Components closer than 2×rMask merge naturally via the dilation. Cached per
   (radius:smoothing) so the animation loop reuses them instead of re-dilating. */
function stickerContours(art,rMask,passes){
  art._cc=art._cc||{};
  var key=(Math.round(rMask*2)/2)+':'+passes;
  if(art._cc[key])return art._cc[key];
  if(Object.keys(art._cc).length>32)art._cc={};   /* keep the cache bounded */
  /* Hi-res cutline mask when available (see buildCutlineMask): the IDENTICAL
     pipeline runs on a grid kR (~1.5x) finer, and the finished contour is mapped
     back into 512-mask coordinates before returning — so callers, the cache key,
     mapPt and every export see exactly the coordinate space they always did.
     kx/ky are per-axis because the two rasterisations round independently.
     Constants that encode a PHYSICAL length (border radius, closing cap 28,
     resample spacing) scale by kR so behaviour is preserved in physical terms;
     CUT_BLUR_SIGMA and CUT_PROJ_CLAMP stay in grid units — on a finer grid that
     means physically TIGHTER smoothing and a TIGHTER feature-move budget, which
     is the fidelity gain, not a behaviour change. */
  var hasHi=!!(art.cutMask&&art.cutW&&art.cutH);
  var mw=hasHi?art.cutW:art.maskW,mh=hasHi?art.cutH:art.maskH;
  var srcMask=hasHi?art.cutMask:art.mask;
  var kx=hasHi?art.cutW/art.maskW:1,ky=hasHi?art.cutH/art.maskH:1,kR=(kx+ky)/2;
  var rm=rMask*kR;
  /* Closing radius: bridges/fills concave notches up to ~2×closeR wide where
     nearby regions almost meet. Tied to the border so it scales, capped so a
     single design's broad concavities are preserved. */
  var closeR=rm>0.01?Math.min(rm,28*kR):0;
  var pad=Math.ceil(Math.max(0,rm)+closeR)+3;   /* room for dilate + close */
  var W=mw+pad*2,H=mh+pad*2;
  var pm=new Uint8Array(W*H);
  for(var y=0;y<mh;y++)for(var x=0;x<mw;x++)if(srcMask[y*mw+x])pm[(y+pad)*W+(x+pad)]=1;
  var grown;
  if(rm>0.01){
    grown=dilateMaskByRadius(pm,W,H,rm);
    /* Morphological CLOSING (dilate then erode by closeR): merges near-touching
       regions into one continuous silhouette and removes the deep concave
       notches/valleys left where the offsets just meet. Closing only ADDS
       pixels, so the convex border thickness stays exactly rMask — it does not
       thicken the border globally, and outer tracing ignores interior holes. */
    if(closeR>0.5)grown=erodeMaskByRadius(dilateMaskByRadius(grown,W,H,closeR),W,H,closeR);
  } else {
    grown=pm;
  }
  /* Smooth coverage field of the final region; its 0.5 iso-line is the true
     sub-pixel silhouette. Built once here (cached), never per frame. */
  var cov=gaussBlurField(grown,W,H,CUT_BLUR_SIGMA);
  /* minArea 1: the source mask is already denoised, so keep every component
     (dots, punctuation, thin links) — do NOT re-filter small details here. */
  var raws=traceAllContours(grown,W,H,1),contours=[];
  for(var c=0;c<raws.length;c++){
    var rc=raws[c];if(rc.length<3)continue;   /* need at least a triangle to trace */
    /* Vectorise like an Offset Path: snap each raster trace node onto the smooth
       0.5 iso-line (sub-pixel, de-staircased, features intact because the move is
       clamped), then one light Chaikin pass + even arc-length resample → flowing,
       constant-width curves with no facets, flats or pixel-trace wobble. Identical
       for inner (rMask 0) and outer contours, so Live Preview, Preview Image and
       Print PNG all share the exact same contour. */
    var proj=projectToIso(rc,cov,W,H,0.5,CUT_PROJ_CLAMP);
    for(var k=0;k<proj.length;k++){proj[k].x-=pad;proj[k].y-=pad;}
    /* spacing ×kR keeps node density identical to today in 512-space terms */
    var sm=ensureCCW(resampleClosed(chaikin(proj,CUT_SMOOTH_PASSES),CUT_RESAMPLE_SPACING*kR,CUT_MAX_NODES));
    /* map back to the 512-mask coordinate frame every consumer expects */
    if(hasHi)for(var k2=0;k2<sm.length;k2++){sm[k2].x/=kx;sm[k2].y/=ky;}
    if(sm.length>=3)contours.push(sm);
  }
  if(DGS_DEBUG){
    console.log('[DGS] stickerContours',{rMaskPx:Math.round(rMask*100)/100,
      hiRes:hasHi,kR:Math.round(kR*1000)/1000,
      components:raws.length,kept:contours.length,
      sampleNodes:contours.length?contours[0].length:0});
  }
  art._cc[key]=contours;
  return contours;
}

function ensureCCW(pts){
  var area=0,n=pts.length;
  for(var i=0;i<n;i++){var a=pts[i],b=pts[(i+1)%n];area+=(a.x*b.y)-(b.x*a.y);}
  if(area<0)return pts.slice().reverse();
  return pts;
}

function getContourBounds(pts){
  var mnX=Infinity,mxX=-Infinity,mnY=Infinity,mxY=-Infinity;
  for(var i=0;i<pts.length;i++){
    if(pts[i].x<mnX)mnX=pts[i].x;if(pts[i].x>mxX)mxX=pts[i].x;
    if(pts[i].y<mnY)mnY=pts[i].y;if(pts[i].y>mxY)mxY=pts[i].y;
  }
  return{minX:mnX,maxX:mxX,minY:mnY,maxY:mxY,w:mxX-mnX,h:mxY-mnY};
}

function buildBlobContour(ar){
  var N=64,rx=ar>=1?100:100*ar,ry=ar>=1?100/ar:100,pts=[];
  for(var i=0;i<N;i++){
    var a=(Math.PI*2*i)/N,w2=1+Math.sin(a*3+.4)*.06+Math.cos(a*5+1.1)*.04;
    pts.push({x:200+Math.cos(a)*rx*w2,y:200+Math.sin(a)*ry*w2});
  }
  return chaikin(pts,3);
}

function extractDominantColours(img){
  var SZ=40,oc=document.createElement('canvas');oc.width=SZ;oc.height=SZ;
  var ox=oc.getContext('2d');ox.drawImage(img,0,0,SZ,SZ);
  var d=ox.getImageData(0,0,SZ,SZ).data,counts={};
  for(var i=0;i<SZ*SZ;i++){
    var r=d[i*4],g=d[i*4+1],b=d[i*4+2],a=d[i*4+3];
    if(a<80)continue;if(r>220&&g>220&&b>220)continue;
    var k=((r>>2)<<12)|((g>>2)<<6)|(b>>2);
    counts[k]=(counts[k]||0)+1;
  }
  var entries=Object.keys(counts).map(function(k){return{k:parseInt(k),c:counts[k]};});
  entries.sort(function(a,b){return b.c-a.c;});
  var cols=[];
  for(var j=0;j<entries.length&&cols.length<6;j++){
    var k=entries[j].k;
    var cr=((k>>12)&0x3f)<<2,cg=((k>>6)&0x3f)<<2,cb=(k&0x3f)<<2;
    var hex='#'+[cr,cg,cb].map(function(c){return('0'+c.toString(16)).slice(-2);}).join('');
    var close=cols.some(function(ex){
      var er=parseInt(ex.slice(1,3),16)-cr,eg=parseInt(ex.slice(3,5),16)-cg,eb=parseInt(ex.slice(5,7),16)-cb;
      return Math.sqrt(er*er+eg*eg+eb*eb)<38;
    });
    if(!close)cols.push(hex);
  }
  return cols;
}

/* ── Hi-res cutline mask (contour sampling fidelity only) ─────────────────
   Re-rasterises the SOURCE image at CUT_MASK_RES and applies the SAME per-pixel
   rule the 512 analysis already chose — alpha>=128 for real-alpha sources, or
   the identical buildWhiteRemovalMask test for white-removed ones. No decision
   is made here: separability, white removal, denoising and component retention
   were all settled at 512 and stay authoritative.

   The GATE enforces that: a hi-res pixel survives only if a 512 cleanMask pixel
   exists in the 3x3 neighbourhood of its parent cell. Specks the 512 denoise
   removed stay removed; components it kept stay kept — identical component set
   BY CONSTRUCTION. Only the edge positions between those components gain
   sub-pixel fidelity, because the source's anti-aliased edges land on a grid
   1.5x finer before thresholding.

   Returns null — and stickerContours falls back to the 512 path, byte-identical
   behaviour — when the source is no bigger than the detection mask (no new
   information to sample) or when the hi-res white-removal disagrees with the
   512 decision (never silently diverge from the authoritative analysis). */
function buildCutlineMask(img,md,cleanMask,mw,mh){
  if(Math.max(img.width,img.height)<=520)return null;   /* nothing to gain */
  var sc=Math.min(1,CUT_MASK_RES/Math.max(img.width,img.height));
  var w=Math.max(4,Math.round(img.width*sc)),h=Math.max(4,Math.round(img.height*sc));
  var oc=document.createElement('canvas');oc.width=w;oc.height=h;
  var ox=oc.getContext('2d',{willReadFrequently:true});
  ox.clearRect(0,0,w,h);ox.drawImage(img,0,0,w,h);
  var d=ox.getImageData(0,0,w,h).data;
  var mask;
  if(md.source==='alpha'){
    mask=new Uint8Array(w*h);
    for(var i=0;i<w*h;i++)if(d[i*4+3]>=128)mask[i]=1;
  } else if(md.whiteRemoved){
    mask=buildWhiteRemovalMask(d,w,h);
    if(!mask)return null;                 /* hi-res disagrees — fall back */
  } else return null;
  /* gate: parent-cell 3x3 against the authoritative 512 cleanMask */
  var kx=w/mw,ky=h/mh;
  for(var y=0;y<h;y++){
    var py=(y/ky)|0;
    for(var x=0;x<w;x++){
      if(!mask[y*w+x])continue;
      var px=(x/kx)|0,ok=false;
      for(var gy=py-1;gy<=py+1&&!ok;gy++){
        if(gy<0||gy>=mh)continue;
        for(var gx=px-1;gx<=px+1;gx++){
          if(gx<0||gx>=mw)continue;
          if(cleanMask[gy*mw+gx]){ok=true;break;}
        }
      }
      if(!ok)mask[y*w+x]=0;
    }
  }
  return {mask:mask,w:w,h:h};
}

function analyseArtwork(img,smoothPasses){
  var md=buildAlphaMask(img),mask=md.mask,mw=md.w,mh=md.h;
  /* "Separable" = a real foreground/background split exists (alpha or white-removed). */
  var separable=md.opaque>20&&md.transparent>20;
  var ar=img.width/img.height,rawPts,outAspect,cropRect=null,usedBlob=false,blobReason='',compCount=0,cleanMask=null;
  if(!separable){
    usedBlob=true;blobReason='no usable foreground/background separation (source='+md.source+', opaque='+md.opaque+', transparent='+md.transparent+')';
    rawPts=buildBlobContour(ar);outAspect=ar;
  } else {
    /* Keep ALL visible artwork (every 8-connected component), removing only
       tiny anti-alias specks. Multi-part designs — logo + text, flail + chain,
       separated lettering — are all retained, not just the largest blob. */
    var den=denoiseMask(mask,mw,mh,2);
    cleanMask=den.mask;compCount=den.kept;
    var bb=maskBounds(cleanMask,mw,mh);
    if(!bb||den.kept<8){
      usedBlob=true;cleanMask=null;
      blobReason='no usable artwork pixels after denoise (kept='+den.kept+')';
      rawPts=buildBlobContour(ar);outAspect=ar;
    } else {
      cropRect={x:bb.minX,y:bb.minY,w:bb.w,h:bb.h};   /* bounds of the WHOLE design */
      outAspect=bb.w>0.01?bb.w/Math.max(bb.h,.01):ar;
      /* one representative contour, used only for blob-fallback / legacy paths */
      rawPts=traceContour(cleanMask,mw,mh);
      if(rawPts.length<8)rawPts=buildBlobContour(outAspect);
    }
  }
  var rawCount=rawPts.length;
  /* Keep far more detail for real traces; blobs stay coarse. */
  var TARGET=usedBlob?120:240;
  var passes=typeof smoothPasses==='number'?smoothPasses:3;
  var samp=subsample(rawPts,TARGET),sm=chaikin(samp,passes),pts=ensureCCW(sm);
  /* Complexity heuristic: spiky/thin outline (high perimeter vs area), many
     isolated pieces, or an unusually long raw contour → flag for manual review. */
  var complex=false;
  if(!usedBlob){
    var perimRatio=compCount>0?rawCount/Math.sqrt(compCount):0;
    complex=perimRatio>9.5||(md.opaque>0&&compCount<md.opaque*0.8)||rawCount>7000;
  }
  if(DGS_DEBUG){
    console.log('[DGS] analyseArtwork',{
      imageWH:img.width+'×'+img.height,maskWH:mw+'×'+mh,
      maskSource:md.source,whiteRemoved:md.whiteRemoved,
      alphaMin:md.alphaMin,alphaMax:md.alphaMax,
      opaquePixels:md.opaque,transparentPixels:md.transparent,
      largestComponentPixels:compCount,
      visibleBounds:cropRect,
      contourPointsBeforeSmoothing:rawCount,sampledTo:samp.length,smoothPasses:passes,
      contourPointsAfterSmoothing:pts.length,
      usedBlobFallback:usedBlob,blobFallbackReason:usedBlob?blobReason:'(not used — real contour traced)'
    });
  }
  /* hi-res cutline mask: only when the 512 analysis produced a real cleanMask
     (blob fallback keeps cut=null and every downstream path is unchanged) */
  var cut=(!usedBlob&&cleanMask)?buildCutlineMask(img,md,cleanMask,mw,mh):null;
  return{outlinePoints:pts,outlineAspect:outAspect,hasTransparency:!!cropRect,
         cropRect:cropRect,maskW:mw,maskH:mh,cleanMask:usedBlob?null:cleanMask,
         cutMask:cut?cut.mask:null,cutW:cut?cut.w:0,cutH:cut?cut.h:0,
         wasWhiteRemoved:md.whiteRemoved&&!usedBlob,complex:complex,
         dominantColours:extractDominantColours(img)};
}

/* ═══════════════════════════════════════════════════════════════════
   GEOMETRY HELPERS
═══════════════════════════════════════════════════════════════════ */
function circlePoints(cx,cy,r,n){
  var pts=[];for(var i=0;i<n;i++){var a=(Math.PI*2*i)/n;pts.push({x:cx+Math.cos(a)*r,y:cy+Math.sin(a)*r});}
  return pts;
}
function roundRectPoints(cx,cy,hw,hh,cr,n){
  /* Rounded rectangle as polygon, corner radius cr */
  var pts=[];
  cr=Math.min(cr,hw,hh);
  var corners=[[-1,-1],[1,-1],[1,1],[-1,1]];
  var segs=Math.max(4,Math.round(n/4));
  corners.forEach(function(c){
    var ox2=c[0]*(hw-cr),oy2=c[1]*(hh-cr);
    for(var i=0;i<segs;i++){
      var startA=Math.atan2(c[1],c[0])-Math.PI/4;
      var a=startA+(i/segs)*(Math.PI/2);
      pts.push({x:cx+ox2+Math.cos(a)*cr,y:cy+oy2+Math.sin(a)*cr});
    }
  });
  return pts;
}

/* ═══════════════════════════════════════════════════════════════════
   RENDERER
═══════════════════════════════════════════════════════════════════ */
function getStickerGeometry(){
  var art=getActive();
  if(!art)return null;

  var AREA=CANVAS_RES-CANVAS_PAD*2;
  var stickerAspect;
  if(S.shape==='circle'||S.shape==='square') stickerAspect=1;
  else if(S.shape==='rectangle'){
    /* Custom size → user W:H. Otherwise match the artwork's exact content aspect
       (not the rounded mm) so the rectangle hugs the artwork with no side gaps. */
    stickerAspect=(S.sizePreset==='custom')?(S.widthMm/(S.heightMm||1)):(art.outlineAspect||(S.widthMm/(S.heightMm||1)));
  }
  else stickerAspect=art.outlineAspect||1;

  /* Sticker shape canvas size — NOT scaled by artScale; border/cutline live outside this */
  var fitW,fitH;
  if(stickerAspect>=1){fitW=AREA;fitH=AREA/stickerAspect;}
  else{fitH=AREA;fitW=AREA*stickerAspect;}

  var originX=(CANVAS_RES-fitW)/2,originY=(CANVAS_RES-fitH)/2;
  var longestPx=Math.max(fitW,fitH);
  var mmToPx=longestPx/S.longestEdgeMm;
  var borderPx=S.borderMm*mmToPx;

  var innerPts,outerPts;
  var cx=CANVAS_RES/2,cy=CANVAS_RES/2;

  /* Source crop rect for drawImage (defaults to full image) */
  var srcX=0,srcY=0,srcW=art.img.width,srcH=art.img.height;
  var drawX,drawY,drawW,drawH;

  if(S.shape==='custom'){
    var pts=art.outlinePoints;
    /* Map bounds: use stored cropRect for transparent PNGs so contour and
       drawImage use the exact same coordinate origin. Blob fallback uses
       smoothed-contour bounds (synthetic coords, no real image to crop). */
    var mb;
    if(art.hasTransparency&&art.cropRect&&art.maskW){
      mb={minX:art.cropRect.x,minY:art.cropRect.y,w:art.cropRect.w,h:art.cropRect.h};
      /* Crop source image to the visible content area */
      var mww=art.maskW,mhh=art.maskH;
      srcX=art.cropRect.x/mww*art.img.width;
      srcY=art.cropRect.y/mhh*art.img.height;
      srcW=art.cropRect.w/mww*art.img.width;
      srcH=art.cropRect.h/mhh*art.img.height;
    } else {
      var cb=getContourBounds(pts);
      mb={minX:cb.minX,minY:cb.minY,w:cb.w,h:cb.h};
    }
    var mapPt=function(p){
      var tx=mb.w>0.01?(p.x-mb.minX)/mb.w:0.5;
      var ty=mb.h>0.01?(p.y-mb.minY)/mb.h:0.5;
      return{x:originX+tx*fitW,y:originY+ty*fitH};
    };
    /* Inner + outer cutline are built from the UNION of ALL artwork components.
       Inner = each component outline (rMask 0); outer = each grown by borderPx.
       Both are arrays of contours so every island is included; components closer
       than 2×border merge into one cut via the dilation. */
    if(art.mask&&art.maskW&&art.maskH&&mb.w>0.01){
      var scaleMC=fitW/mb.w;                 /* canvas px per mask px */
      var rMask=scaleMC>0?borderPx/scaleMC:0;/* border radius in mask px */
      var innerC=stickerContours(art,0,S.smoothPasses);
      var outerC=stickerContours(art,rMask,S.smoothPasses);
      if(innerC.length&&outerC.length){
        innerPts=innerC.map(function(cc){return ensureCCW(cc.map(mapPt));});
        outerPts=outerC.map(function(cc){return ensureCCW(cc.map(mapPt));});
      } else {
        var single=ensureCCW(pts.map(mapPt));
        innerPts=[single];outerPts=[offsetPolygon(single,borderPx)];
      }
    } else {
      var s2=ensureCCW(pts.map(mapPt));       /* blob fallback (no mask) */
      innerPts=[s2];outerPts=[offsetPolygon(s2,borderPx)];
    }
    /* artScale zooms the artwork image inside the die-cut area */
    drawW=fitW*S.artScale;drawH=fitH*S.artScale;
    drawX=(CANVAS_RES-drawW)/2;drawY=(CANVAS_RES-drawH)/2;
  } else {
    /* Geometric shapes — reserve the border first, then CONTAIN-fit the
       artwork's VISIBLE content inside the remaining safe area. Cropping to the
       content (cropRect) means transparent PNG padding no longer shrinks the
       artwork, so it fills the shape properly. */
    var imgAr=art.outlineAspect||(art.img.width/(art.img.height||1));
    if(art.hasTransparency&&art.cropRect&&art.maskW&&art.maskH){
      srcX=art.cropRect.x/art.maskW*art.img.width;
      srcY=art.cropRect.y/art.maskH*art.img.height;
      srcW=art.cropRect.w/art.maskW*art.img.width;
      srcH=art.cropRect.h/art.maskH*art.img.height;
    }
    /* Corner Roundness (0–5): 0 = subtle factory corner (never perfectly sharp),
       5 = maximum rounded. Uses its own cornerRadius value, independent of the
       custom die-cut contour smoothing. */
    var cf=CORNER_MIN+(Math.max(0,Math.min(5,S.cornerRadius))/5)*(CORNER_MAX-CORNER_MIN);
    if(S.shape==='circle'){
      var R=Math.max(20,fitW/2-borderPx);            /* safe inner radius after border */
      innerPts=circlePoints(cx,cy,R,120);
      outerPts=circlePoints(cx,cy,R+borderPx,120);
      /* Inscribed rectangle (diag=2R) is the zero-crop maximum, but it leaves the
         circle looking empty. Push slightly past it (CIRCLE_FILL) so the artwork
         fills the circle visually — corners get gently rounded off by the clip. */
      var CIRCLE_FILL=1.12;
      var diag=2*R,fh=diag/Math.sqrt(imgAr*imgAr+1)*CIRCLE_FILL,fw=fh*imgAr;
      drawW=fw*S.artScale;drawH=fh*S.artScale;
    } else if(S.shape==='square'){
      var half=Math.max(20,fitW/2-borderPx),cr2=half*cf;
      innerPts=roundRectPoints(cx,cy,half,half,cr2,64);
      outerPts=roundRectPoints(cx,cy,half+borderPx,half+borderPx,cr2+borderPx*.5,64);
      var side=2*half;
      if(imgAr>=1){drawW=side*S.artScale;drawH=drawW/imgAr;}
      else{drawH=side*S.artScale;drawW=drawH*imgAr;}
    } else { /* rectangle */
      var hw=Math.max(20,fitW/2-borderPx),hh=Math.max(20,fitH/2-borderPx),cr3=Math.min(hw,hh)*cf;
      innerPts=roundRectPoints(cx,cy,hw,hh,cr3,64);
      outerPts=roundRectPoints(cx,cy,hw+borderPx,hh+borderPx,cr3+borderPx*.5,64);
      var rw=2*hw,rh=2*hh,rectAr=rw/(rh||1);
      if(imgAr>=rectAr){drawW=rw*S.artScale;drawH=drawW/imgAr;}
      else{drawH=rh*S.artScale;drawW=drawH*imgAr;}
    }
    drawX=(CANVAS_RES-drawW)/2;drawY=(CANVAS_RES-drawH)/2;
  }

  return{innerPts:innerPts,outerPts:outerPts,originX:originX,originY:originY,
         fitW:fitW,fitH:fitH,drawX:drawX,drawY:drawY,drawW:drawW,drawH:drawH,
         srcX:srcX,srcY:srcY,srcW:srcW,srcH:srcH,borderPx:borderPx,mmToPx:mmToPx};
}

function buildPath(points,c){
  c.beginPath();
  if(!points||!points.length)return;
  /* Multi-contour: array of contours (custom shape with multiple islands). */
  if(Array.isArray(points[0])){
    for(var k=0;k<points.length;k++){
      var sub=points[k];
      if(!sub||sub.length<2)continue;
      c.moveTo(sub[0].x,sub[0].y);
      for(var j=1;j<sub.length;j++)c.lineTo(sub[j].x,sub[j].y);
      c.closePath();
    }
    return;
  }
  /* Single contour (geometric shapes). */
  c.moveTo(points[0].x,points[0].y);
  for(var i=1;i<points.length;i++)c.lineTo(points[i].x,points[i].y);
  c.closePath();
}

/* Live-preview glossy sheen — white diagonal light sweep, clipped to the sticker
   shape, drawn ABOVE the artwork/border but BELOW the cutline. On-screen only;
   exports pass {sheen:false} so it never appears in any downloaded file. Future:
   branch the gradient here by material (gloss/clear/holographic/matte). */
function drawSheen(geo){
  var t=(Date.now()%SHEEN_CYCLE)/SHEEN_CYCLE;   /* 0→1 each cycle */
  var c=-0.3+t*1.6;                              /* main band centre sweeps off-screen→off-screen */
  ctx.save();
  buildPath(geo.outerPts,ctx);                   /* clip to the whole sticker (incl. border) */
  ctx.clip();
  ctx.globalCompositeOperation='screen';         /* brighten like a reflection, not flat white */
  /* 1. secondary ambient reflection — wider, softer, much lower opacity; trails behind */
  ctx.fillStyle=sheenBand(c-SHEEN_TRAIL,SHEEN_W2,SHEEN_PEAK2);
  ctx.fillRect(0,0,CANVAS_RES,CANVAS_RES);
  /* 2. main highlight — crisper + brighter, with faded shoulders so edges stay soft */
  ctx.fillStyle=sheenBand(c,SHEEN_W1,SHEEN_PEAK);
  ctx.fillRect(0,0,CANVAS_RES,CANVAS_RES);
  ctx.restore();                                  /* resets clip + composite */
}

/* A soft diagonal white band (TL→BR) centred at `center` (0–1 along the diagonal),
   half-width `w`, peak white opacity `peak`. A short bright core with faded
   shoulders keeps it crisp yet soft; an off-screen centre yields a fully clear band.
   Canvas sorts colour stops, so positions can be added in any order. */
function sheenBand(center,w,peak){
  var g=ctx.createLinearGradient(0,0,CANVAS_RES,CANVAS_RES);
  function stop(p,a){ if(p>=0&&p<=1) g.addColorStop(p,'rgba(255,255,255,'+a+')'); }
  stop(0,0); stop(1,0);
  stop(center-w,0);
  stop(center-w*0.45,+(peak*0.55).toFixed(3));
  stop(center,+peak.toFixed(3));
  stop(center+w*0.45,+(peak*0.55).toFixed(3));
  stop(center+w,0);
  return g;
}

function render(antOffset,opts){
  var art=getActive();
  /* Size the backing store ONCE, not on every frame. Assigning canvas.width
     reallocates the bitmap and resets the whole 2D context (transform, clip,
     styles, state stack) — at 60fps that was pure waste, because CANVAS_RES is
     a constant and nothing else ever resizes this canvas. The assignment cannot
     simply be deleted: the <canvas> element carries no width/height attributes,
     so it starts at the UA default 300x150 and the first call must still set it.
     Everything the reset used to provide is already guaranteed:
       - the cleared bitmap comes from the clearRect below (unchanged);
       - context state cannot leak between frames — every ctx.save() in render()
         and drawSheen() is matched by a ctx.restore(), buildPath() always calls
         beginPath(), and every style is assigned immediately before it is used.
     Pixel output is therefore identical. */
  if(canvasEl.width!==CANVAS_RES||canvasEl.height!==CANVAS_RES){
    canvasEl.width=CANVAS_RES;
    canvasEl.height=CANVAS_RES;
  }
  ctx.clearRect(0,0,CANVAS_RES,CANVAS_RES);

  if(!art){
    /* No artwork — show nothing on canvas, overlay visible */
    return;
  }

  var geo=getStickerGeometry();
  if(!geo||!geo.innerPts||!geo.innerPts.length||!geo.outerPts||!geo.outerPts.length)return;

  /* Sticker shadow — cast from the actual outer sticker silhouette (shape-following:
     die-cut, circle, square, rectangle, rounded corners — never a canvas rectangle).
     Subtle, neutral and slightly downward so the sticker floats above the grid. Drawn
     in render(), so it appears in the live preview AND the Preview Image, but NOT in the
     Print PNG (which never calls render()). */
  ctx.save();
  ctx.shadowColor='rgba(0,0,0,.18)';   /* low opacity, no colour tint, no harsh halo */
  ctx.shadowBlur=20;                    /* gentle feathered edge */
  ctx.shadowOffsetY=9;                  /* small downward offset */
  buildPath(geo.outerPts,ctx);
  ctx.fillStyle='#ffffff';
  ctx.fill();
  ctx.restore();

  /* White/coloured border fill */
  buildPath(geo.outerPts,ctx);
  ctx.fillStyle=S.borderColour;
  ctx.fill();

  /* Sticker fill / background — fills the shape behind the artwork.
     'none' leaves the border colour showing through (previous behaviour). */
  if(S.fillColour&&S.fillColour!=='none'){
    buildPath(geo.innerPts,ctx);
    ctx.fillStyle=S.fillColour;
    ctx.fill();
  }

  /* Artwork clipped to inner contour — source-cropped, aspect preserved */
  ctx.save();
  buildPath(geo.innerPts,ctx);
  ctx.clip();
  ctx.drawImage(art.img,geo.srcX,geo.srcY,geo.srcW,geo.srcH,geo.drawX,geo.drawY,geo.drawW,geo.drawH);
  ctx.restore();

  /* Live-preview glossy sheen — ONLY when explicitly requested (live loop), enabled,
     and reduced-motion is off. Exports pass {sheen:false}, so never in a downloaded file. */
  if(opts&&opts.sheen&&SHEEN_LIVE&&!(sheenReducedMQL&&sheenReducedMQL.matches))drawSheen(geo);

  /* Animated marching ants cutline — dark shadow pass then brand gradient */
  var ao=antOffset||0;
  buildPath(geo.outerPts,ctx);
  ctx.save();
  /* 1. Dark shadow underneath */
  ctx.strokeStyle='rgba(0,0,0,.55)';
  ctx.lineWidth=6;
  ctx.setLineDash([16,10]);
  ctx.lineDashOffset=ao;
  ctx.stroke();
  /* 2. Brand orange→yellow gradient on top */
  var grad=ctx.createLinearGradient(0,0,CANVAS_RES,CANVAS_RES);
  grad.addColorStop(0,TOK.orange);
  grad.addColorStop(0.5,TOK.yellow);
  grad.addColorStop(1,TOK.orange);
  ctx.strokeStyle=grad;
  ctx.lineWidth=4;
  ctx.setLineDash([16,10]);
  ctx.lineDashOffset=ao;
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();

  /* DEBUG overlay: visualise the three contours so the offset can be verified.
     Set DGS_DEBUG=false to remove. */
  if(DGS_DEBUG){
    ctx.save();
    ctx.setLineDash([]);
    /* original traced artwork contour — cyan */
    buildPath(geo.innerPts,ctx);
    ctx.strokeStyle='rgba(0,229,255,.95)';ctx.lineWidth=1.5;ctx.stroke();
    /* expanded border mask contour = final cutline — magenta */
    buildPath(geo.outerPts,ctx);
    ctx.strokeStyle='rgba(255,0,200,.95)';ctx.lineWidth=1.5;ctx.stroke();
    /* labels */
    ctx.font='600 16px Helvetica,Arial,sans-serif';ctx.textBaseline='top';
    ctx.fillStyle='rgba(0,229,255,.95)';ctx.fillText('— original artwork contour',18,18);
    ctx.fillStyle='rgba(255,0,200,.95)';ctx.fillText('— expanded border mask contour = final cutline',18,40);
    ctx.restore();
  }
}

/* Marching ants animation loop */
function startAnimation(){
  if(S.rafId)return;
  var offset=0;
  function tick(){
    if(!getActive()){S.rafId=null;return;}
    offset=(offset+0.4)%24;
    render(offset,{sheen:true});   /* live preview gets the glossy sheen */
    S.rafId=requestAnimationFrame(tick);
  }
  S.rafId=requestAnimationFrame(tick);
}

function stopAnimation(){
  if(S.rafId){cancelAnimationFrame(S.rafId);S.rafId=null;}
}

/* ═══════════════════════════════════════════════════════════════════
   ARTWORK MANAGEMENT
═══════════════════════════════════════════════════════════════════ */
function getActive(){
  if(!S.activeId)return null;
  for(var i=0;i<S.artworks.length;i++)if(S.artworks[i].id===S.activeId)return S.artworks[i];
  return null;
}

/* ── per-artwork design settings ──────────────────────────────────────────
   Each artwork carries its own design (shape/size/border/etc). S mirrors the
   ACTIVE artwork's design; we read it back into the artwork on every edit and
   load the artwork's own design when it becomes active. */
function defaultDesign(){
  return {shape:'custom',sizePreset:'75',customW:75,customH:75,customEdge:75,
          borderMm:2.5,smoothPasses:3,cornerRadius:DEFAULT_CORNER,
          borderColour:'#ffffff',fillColour:'none',
          artScale:1.0,rectW:100,rectH:60};
}
/* Pick the most sensible DEFAULT shape from the existing analysis (no extra
   detection). hasTransparency is true for real alpha (Rule 1) AND for artwork
   whose white background was successfully removed (Rule 2) → Custom. Anything
   opaque (solid colour background or photo, Rules 3/4) → Square if roughly
   square, otherwise Rectangle. The customer can still change it freely. */
function autoDefaultShape(art){
  if(!art)return 'custom';
  if(art.hasTransparency)return 'custom';
  var ar=art.img.width/(art.img.height||1);
  return (ar>=0.85&&ar<=1.18)?'square':'rectangle';
}
function readDesign(){
  return {shape:S.shape,sizePreset:S.sizePreset,customW:S.customW,customH:S.customH,customEdge:S.customEdge,
          borderMm:S.borderMm,smoothPasses:S.smoothPasses,cornerRadius:S.cornerRadius,
          borderColour:S.borderColour,
          fillColour:S.fillColour,artScale:S.artScale,rectW:S.rectW,rectH:S.rectH};
}
function applyDesign(d){
  d=d||defaultDesign();
  S.shape=d.shape;S.sizePreset=d.sizePreset;S.customW=d.customW;S.customH=d.customH;
  S.customEdge=clampCustomEdge(typeof d.customEdge==='number'?d.customEdge:75);
  S.borderMm=d.borderMm;S.smoothPasses=d.smoothPasses;S.borderColour=d.borderColour;
  S.cornerRadius=(typeof d.cornerRadius==='number'?d.cornerRadius:DEFAULT_CORNER);
  S.fillColour=(d.fillColour||'none');S.artScale=d.artScale;S.rectW=d.rectW;S.rectH=d.rectH;
  refreshColourModes();   /* derive custom latches from the loaded colours */
  syncControlsToState();
}
/* write the live controls' state back onto the active artwork */
/* commitActive() is the shared write path for EVERY editable setting (shape,
   size, custom dimensions, border width, border colour, artwork scale, corner
   roundness) — 12 call sites — so bumping the project revision here covers them
   all without a single new control listener. The bump sits INSIDE the existing
   `if(a)` guard on purpose: with no artwork there is no project, so touching
   controls before uploading can never arm the leave warning. */
function commitActive(){var a=getActive();if(a){a.settings=readDesign();dgsMarkProjectChanged();}}
/* push S design fields out to every control's DOM (no input events fired) */
function syncControlsToState(){
  root.querySelectorAll('[data-dgs-shape]').forEach(function(b){b.classList.toggle('active',b.getAttribute('data-dgs-shape')===S.shape);});
  root.querySelectorAll('[data-dgs-size]').forEach(function(b){b.classList.toggle('active',b.getAttribute('data-dgs-size')===S.sizePreset);});
  refreshCustomSizeUI();
  borderSlider.value=String(S.borderMm);borderVal.textContent=S.borderMm+'mm';
  root.querySelectorAll('[data-dgs-border-preset]').forEach(function(b){b.classList.toggle('active',parseFloat(b.getAttribute('data-dgs-border-preset'))===S.borderMm);});
  smoothSlider.value=String(S.cornerRadius);smoothVal.textContent=S.cornerRadius+'mm';
  root.querySelectorAll('[data-dgs-smooth-preset]').forEach(function(b){b.classList.toggle('active',parseInt(b.getAttribute('data-dgs-smooth-preset'),10)===S.cornerRadius);});
  syncBorderColourUI();
  syncFillUI();
  var pct=Math.round(S.artScale*100);scaleSlider.value=String(pct);scaleVal.textContent=pct+'%';
  applyBorderConstraints();
}

/* Die-cut requires a minimum 2mm border for a clean, durable cut. "None" stays
   visible but disabled for custom; all options remain for geometric shapes. */
function applyBorderConstraints(){
  var isCustom=S.shape==='custom';
  var TIP='Custom stickers require a minimum 2mm border to ensure a clean cut path and durable finished sticker.';
  var noneChip=root.querySelector('[data-dgs-border-preset="0"]');
  if(noneChip){noneChip.disabled=isCustom;noneChip.title=isCustom?TIP:'';}
  /* same rule for the Border Colour "None" chip */
  var noneCol=root.querySelector('[data-dgs-bordercol="none"]');
  if(noneCol){noneCol.disabled=isCustom;noneCol.title=isCustom?TIP:'';}
  borderSlider.min=isCustom?'2':'0';
  if(isCustom&&S.borderMm<2){
    S.borderMm=2;
    borderSlider.value='2';borderVal.textContent='2mm';
    root.querySelectorAll('[data-dgs-border-preset]').forEach(function(b){
      b.classList.toggle('active',parseFloat(b.getAttribute('data-dgs-border-preset'))===S.borderMm);
    });
  }
  /* Smoothing is only meaningful for Square/Rectangle (corner roundness).
     Custom (die-cut) and Circle don't need it, so hide the whole control. */
  var showSmooth=(S.shape==='square'||S.shape==='rectangle');
  var smoothSection=root.querySelector('[data-dgs-smooth-section]');
  if(smoothSection)smoothSection.style.display=showSmooth?'':'none';
  var smoothLabel=root.querySelector('[data-dgs-smooth-label]');
  if(smoothLabel)smoothLabel.textContent='Corner Roundness';
  smoothSlider.disabled=!showSmooth;
  root.querySelectorAll('[data-dgs-smooth-preset]').forEach(function(b){b.disabled=!showSmooth;});
  refreshHelpTips();   /* shape-dependent help copy — see below */
  /* borderSlider.min was just rewritten above, which changes the percentage
     denominator even when the value itself did not move — so the fill has to be
     recomputed here. This call also covers syncControlsToState() and
     fullRefresh(), which both end by calling applyBorderConstraints(), and the
     INIT block, which calls it directly. */
  dgsSyncSliderFill();
}

/* ── slider progress fill (presentation only) ──────────────────────────────
   WebKit has no ::-moz-range-progress equivalent, so the filled portion of the
   track is painted by the track's own gradient with a hard stop at
   --dgs-slider-pos. This writes that ONE custom property per input; every
   colour lives in the CSS.

   It READS value/min/max and writes nothing back — no value, no min, no max,
   no step, no snapping, no label and no studio state is touched, so it cannot
   affect what any control does. Firefox uses its native ::-moz-range-progress
   and simply ignores the property.

   All three sliders (Border Width, Corner Roundness, Artwork Scale) are handled
   by the same pass; they share the .dgs-slider class, so there is one
   implementation and nothing per-slider to keep in sync. Guarded against a
   zero or non-numeric range so it can never emit NaN. */
function dgsSyncSliderFill(){
  root.querySelectorAll('.dgs-slider').forEach(function(sl){
    var min=parseFloat(sl.min), max=parseFloat(sl.max), val=parseFloat(sl.value);
    if(!isFinite(min)||!isFinite(max)||!isFinite(val)||max===min){
      sl.style.setProperty('--dgs-slider-pos','0%');return;
    }
    var pct=(val-min)/(max-min)*100;
    if(pct<0)pct=0; else if(pct>100)pct=100;
    sl.style.setProperty('--dgs-slider-pos',(Math.round(pct*100)/100)+'%');
  });
}

/* ── contextual help content ──────────────────────────────────────────────
   CONTENT ONLY. Showing and hiding is pure CSS — the control's own :hover /
   :focus-within reveals the tip that follows it — so there is no tooltip engine,
   no positioning code and no event listeners.

   Hooked to applyBorderConstraints() because that already runs at exactly the
   three moments the copy can go stale — on init, on every shape change (via
   fullRefresh) and on artwork switch (via syncControlsToState) — so one call
   site covers all of them.

   Both tips describe what the CONTROL does. The stage tip separately reports
   what the current setup RESULTS in, so the two surfaces never say the same
   thing. Reads only existing state: S.shape and the artwork flags
   analyseArtwork already stored. No new analysis, no thresholds, no
   recalculation, and nothing here can alter a customer's selection. */
var dgsTipSync=null;   /* set by dgsTooltips(); refreshes an open floating bubble */
function refreshHelpTips(){
  var art=getActive();
  var shapeTip=root.querySelector('[data-dgs-tip-shape]');
  var borderTip=root.querySelector('[data-dgs-tip-border]');
  if(shapeTip){
    shapeTip.textContent=(S.shape==='custom'&&art&&!art.hasTransparency)
      ? 'Custom needs a transparent background to follow your artwork. With a solid background, choose square or rectangle.'
      : 'Custom follows your artwork’s outline. Circle, square and rectangle place it inside a fixed shape.';
  }
  if(borderTip){
    borderTip.textContent=(S.shape==='custom')
      ? 'The edge around your sticker. Custom shapes need at least 2mm so the cut stays clean and durable.'
      : 'The edge around your sticker. Choose none, or up to 5mm — on fixed shapes it is purely visual.';
  }
  if(dgsTipSync)dgsTipSync();   /* presentation only — re-reads the text above */
}

/* Set by the summary-toggle IIFE below, which owns buildThumbs(). Null until
   then, and a no-op whenever the panels are collapsed. */
var dgsThumbSync=null;

function activateArtwork(id){
  S.activeId=id;
  resetView();                        /* pan/zoom is per-session viewer state — reset on switch */
  var art=getActive();
  if(art){
    applyDesign(art.settings);        /* load THIS artwork's own settings + controls */
    computeDimensions();
    uploadOverlay.classList.add('hidden');
    canvasEl.style.display='block';
    startAnimation();
    fadeCanvasSwap();                 /* presentation only — no state touched */
  } else {
    applyDesign(defaultDesign());     /* no artwork: reset controls to defaults */
    S.widthMm=75;S.heightMm=75;S.longestEdgeMm=75;
    uploadOverlay.classList.remove('hidden');
    canvasEl.style.display='none';
    stopAnimation();
    render(0);
  }
  refreshSlots();
  refreshBadges();
  refreshSummary();
  /* The summary VALUES above are already live on switch. The two Downloads
     thumbnails were not: buildThumbs() is private to the summary-toggle IIFE and
     only ran on the collapsed->expanded transition, so switching artwork left
     both images showing the previous artwork until the panel was reopened.
     Repaint them here, in place, only while the panels are actually expanded. */
  if(dgsThumbSync)dgsThumbSync();
  /* mobile carousel: switching the active artwork can move it off-screen (e.g.
     removing a card auto-activates a different one) — recentre it so "active"
     still reads visually as the main card. No-op on desktop. */
  if(window.matchMedia('(max-width:800px)').matches){
    var activeSlotEl=slotsRow.querySelector('.dgs-slot.is-active');
    if(activeSlotEl)activeSlotEl.scrollIntoView({behavior:'smooth',inline:'center',block:'nearest'});
  }
  /* Switching the active artwork changes which project is on screen, so the
     revision moves and downloads taken for the previous one no longer apply. */
  dgsMarkProjectChanged();
}

function removeArtwork(id){
  S.artworks=S.artworks.filter(function(a){return a.id!==id;});
  if(S.activeId===id)S.activeId=S.artworks.length?S.artworks[0].id:null;
  activateArtwork(S.activeId);
  /* Removing ONE of several artworks changes the project, so the revision moves
     and any earlier downloads go stale. Removing the LAST one empties the
     project entirely — dgsMarkProjectChanged() neutralises the download stamps
     in that case, so the studio returns to a genuinely unprotected empty state. */
  dgsMarkProjectChanged();
}

/* Reset keeps the artwork but restores all its design settings to defaults
   (shape, size, border, colours, scale, background). The uploaded image and
   its traced contour/mask are untouched. */
function resetArtwork(id){
  var art=null;
  for(var i=0;i<S.artworks.length;i++)if(S.artworks[i].id===id)art=S.artworks[i];
  if(!art)return;
  art.settings=defaultDesign();
  art.settings.shape=art.autoShape||'custom';   /* keep the auto-detected default */
  art._cc=null;   /* drop cached cutline/border contours */
  if(S.activeId===id)activateArtwork(id);  /* re-apply defaults + re-render */
  else refreshSlots();
  /* a reset is a real project change, so previous downloads become stale */
  dgsMarkProjectChanged();
}

/* ═══════════════════════════════════════════════════════════════════
   SLOT RENDERING
═══════════════════════════════════════════════════════════════════ */
function refreshSlots(){
  var html='';
  for(var i=0;i<MAX_SLOTS;i++){
    var art=S.artworks[i]||null;
    var isActive=art&&art.id===S.activeId;
    var label='Artwork '+(i+1);
    if(!art){
      var disabled=S.artworks.length>=MAX_SLOTS?' dgs-slot-empty-disabled':'';
      html+='<div class="dgs-slot'+disabled+'" data-dgs-upload-slot>';
      html+='<div class="dgs-slot-label">'+label+'</div>';
      html+='<div class="dgs-slot-thumb" style="justify-content:center;align-items:center"><span class="dgs-slot-empty-icon">+</span></div>';
      html+='<div><div class="dgs-slot-empty-text">Upload Artwork</div><div class="dgs-slot-empty-sub">PNG / JPG</div></div>';
      html+='</div>';
    } else {
      var dpi=calcDPI(art);
      /* status first, matching the toolbar — both now derive from dpiQuality()
         so they cannot drift. Rendered on the ACTIVE card only (see the
         isActive guard below), so inactive cards stay quiet. */
      var dpiCls=dpiQuality(dpi).cls;
      var dpiTxt=dpiLabel(dpi);
      html+='<div class="dgs-slot'+(isActive?' is-active':'')+'" data-dgs-activate-slot="'+art.id+'">';
      html+='<div class="dgs-slot-label">'+(isActive?'<span class="dgs-slot-active-badge">ACTIVE ARTWORK</span>':label)+'</div>';
      html+='<div class="dgs-slot-thumb"><img src="'+art.url+'" alt=""></div>';
      html+='<div class="dgs-slot-info">';
      html+='<div class="dgs-slot-name">'+escHtml(art.name)+'</div>';
      html+='<div class="dgs-slot-dims">'+art.img.width+' × '+art.img.height+'px</div>';
      if(isActive)html+='<div class="dgs-slot-dpi '+dpiCls+'">'+dpiTxt+'</div>';
      /* Cutline source — active card only, so inactive slots stay quiet. Reuses
         the existing .dgs-slot-dims metadata style rather than introducing a
         component or colour. Neutral in all three states by design: opaque
         artwork is perfectly valid for square/rectangle stickers, so colouring
         it would read as a warning where none exists. */
      if(isActive){
        var cut=cutlineSource(art);
        html+='<div class="dgs-slot-dims dgs-slot-cut" title="'+cut.tip+'">'+cut.label+'</div>';
      }
      html+='</div>';
      if(isActive){
        html+='<div class="dgs-slot-actions">'
          +'<button class="dgs-art-btn" data-dgs-reset-slot="'+art.id+'" onclick="event.stopPropagation()">↺ Reset</button>'
          +'<button class="dgs-art-btn" data-dgs-remove-slot="'+art.id+'" onclick="event.stopPropagation()">✕ Remove</button>'
          +'</div>';
      } else {
        html+='<button class="dgs-art-btn" style="font-size:9px" data-dgs-remove-slot="'+art.id+'" onclick="event.stopPropagation()">✕ Remove</button>';
      }
      html+='</div>';
    }
  }
  slotsRow.innerHTML=html;

  /* bind slot events */
  slotsRow.querySelectorAll('[data-dgs-upload-slot]').forEach(function(el){
    el.addEventListener('click',function(){fileInput.click();});
  });
  slotsRow.querySelectorAll('[data-dgs-activate-slot]').forEach(function(el){
    el.addEventListener('click',function(){activateArtwork(el.getAttribute('data-dgs-activate-slot'));});
  });
  slotsRow.querySelectorAll('[data-dgs-remove-slot]').forEach(function(el){
    el.addEventListener('click',function(e){e.stopPropagation();openConfirm('remove',el.getAttribute('data-dgs-remove-slot'));});
  });
  slotsRow.querySelectorAll('[data-dgs-reset-slot]').forEach(function(el){
    el.addEventListener('click',function(e){e.stopPropagation();openConfirm('reset',el.getAttribute('data-dgs-reset-slot'));});
  });

  /* one live instance remains (the desktop topbar pill — the mobile copy was
     removed by design); the querySelectorAll form is kept so any future
     instance stays in sync automatically and none can ever go stale */
  root.querySelectorAll('[data-dgs-upload-count]').forEach(function(el){
    el.textContent=S.artworks.length+' / '+MAX_SLOTS+' UPLOADED';
  });
  var active=getActive();
  activeName.textContent=active?active.name:'No artwork selected';

  /* empty-state: clean first-time experience when nothing is uploaded */
  root.classList.toggle('is-empty',!active);

  /* enable/disable download buttons */
  root.querySelectorAll('[data-dgs-dl]').forEach(function(b){b.disabled=!active;});
}

/* ═══════════════════════════════════════════════════════════════════
   DPI
═══════════════════════════════════════════════════════════════════ */
/* ── effective print DPI ───────────────────────────────────────────────────
   STRICTLY READ-ONLY over the existing render geometry. It calls
   getStickerGeometry() and uses only numbers that are already driving the real
   drawImage() call; it sets nothing, caches nothing and cannot alter the
   sticker shape, the contour, the mask or any export. getStickerGeometry()
   assigns to no state — it reads S and returns a fresh object — so calling it
   here is side-effect free apart from populating the contour cache it would
   have populated anyway.

   THE MATHS. render() and buildPrintCanvas() both draw the artwork with

     drawImage(img, srcX,srcY,srcW,srcH, drawX,drawY,drawW,drawH)

   so srcW SOURCE pixels are stretched across drawW CANVAS pixels, and
   geo.mmToPx canvas pixels are one finished millimetre (mmToPx is
   max(fitW,fitH)/S.longestEdgeMm — the sticker's own px-per-mm). Therefore

     source px per mm   = (srcW / drawW) * mmToPx
     source px per inch = (srcW / drawW) * mmToPx * 25.4

   That one expression already accounts for everything the uploaded-file figure
   missed, because each factor is the value the renderer itself uses:

     CROPPING  srcW/srcH come from art.cropRect mapped back onto the source, so
               transparent padding is excluded from the numerator exactly as it
               is excluded from the drawn artwork. A PNG that is 50% padding no
               longer counts the padding as resolution.
     BORDER    for circle/square/rectangle the artwork is contain-fitted INSIDE
               fitW-2*borderPx, so drawW is smaller than the sticker and the
               artwork spans fewer millimetres than the longest edge. Custom
               die-cut maps the artwork across the full fitW, so it is unchanged
               by this factor — which is correct, that is what it does.
     artSCALE  drawW/drawH are multiplied by S.artScale, so enlarging the
               artwork to 200% halves its effective resolution, and shrinking it
               raises it. Reading it off drawW means artScale can never be
               double-counted or forgotten.

   ORIENTATION needs no special case. The ratio is taken per axis and the LOWER
   of the two is reported, so portrait, landscape and square artwork are each
   measured on their own limiting axis. The two axes agree to within mask
   rounding for every current shape (the source rect and the fitted box share
   the artwork's aspect), so this is a safety floor rather than a behaviour.

   Returns 0 when the geometry is unavailable — no active artwork, or a query
   about an artwork that is not the active one. calcDPI() then falls back to its
   original figure, which is reached ONLY by the inactive slot cards; those
   compute a DPI that refreshSlots() never renders (see the isActive guard), so
   no value a customer can see comes from the fallback. */
function effectivePrintDPI(art){
  if(!art||art!==getActive())return 0;
  var geo=getStickerGeometry();
  if(!geo)return 0;
  if(!(geo.mmToPx>0))return 0;
  if(!(geo.srcW>0)||!(geo.srcH>0)||!(geo.drawW>0)||!(geo.drawH>0))return 0;
  /* source pixels per canvas pixel, on the limiting axis */
  var srcPerCanvasPx=Math.min(geo.srcW/geo.drawW,geo.srcH/geo.drawH);
  var dpi=Math.round(srcPerCanvasPx*geo.mmToPx*25.4);
  return isFinite(dpi)&&dpi>0?dpi:0;
}

function calcDPI(art){
  if(!art)return 0;
  /* Effective PRINT resolution of the artwork actually being drawn. Falls back
     to the original uploaded-file figure only where the geometry cannot answer
     (inactive slot cards, whose DPI is computed but never displayed). */
  var eff=effectivePrintDPI(art);
  if(eff>0)return eff;
  var longestPx=Math.max(art.img.width,art.img.height);
  var longestIn=S.longestEdgeMm/25.4;
  return Math.round(longestPx/longestIn);
}

/* ── quality derivation, single source of truth ────────────────────────────
   THRESHOLDS ARE UNCHANGED (>=300 good, >=150 okay, else low). This exists only
   so the toolbar and the active artwork card stop each writing out their own
   copy of the same ternary — that duplication is exactly how they drifted apart
   and produced "toolbar OKAY · 229 DPI" beside "card GOOD · 305 DPI". */
function dpiQuality(dpi){
  if(dpi>=300)return{cls:'good',word:'GOOD'};
  if(dpi>=150)return{cls:'okay',word:'OKAY'};
  return{cls:'low',word:'LOW'};
}
/* the approved shared format: "GOOD · 394 DPI" */
function dpiLabel(dpi){ return dpiQuality(dpi).word+' · '+dpi+' DPI'; }

/* ── cutline source ────────────────────────────────────────────────────────
   Answers the customer's main die-cut question — "will it cut around my
   design?" — using ONLY flags analyseArtwork() already stored. No new analysis,
   no new thresholds, nothing recalculated.
   PRECEDENCE MATTERS and mirrors the existing stage-tip logic exactly:
   wasWhiteRemoved is tested FIRST because hasTransparency is also true for
   white-removed artwork (both paths set cropRect, and hasTransparency is
   !!cropRect). Testing hasTransparency first would mislabel every
   background-removed upload as a transparent PNG.

   Returns {label, tip}: the visible one-liner plus the plain-English tooltip.
   Wording only — the three branches and their precedence are unchanged. */
function cutlineSource(art){
  if(!art)return{label:'',tip:''};
  if(art.wasWhiteRemoved)return{
    label:'Background removed',
    tip:'A solid white background was automatically removed to create a custom-shaped sticker.'};
  if(art.hasTransparency)return{
    label:'Transparent PNG',
    tip:'Best for custom-shaped stickers. Your cutline follows the visible artwork.'};
  return{
    label:'Solid background',
    tip:'This artwork contains a solid background. Upload a transparent PNG if you want an automatic custom-shaped sticker.'};
}

/* ── targeted active-card DPI refresh ──────────────────────────────────────
   Effective DPI depends on sticker size, which changes far more often than the
   slot strip is rebuilt — refreshSlots() only runs on artwork-level actions
   (upload / switch / reset / remove). Calling it on every size change would
   re-run innerHTML on each keystroke in the custom-size field AND retrigger the
   Phase 2C card entrance animations, so this patches just the one text node
   that can go stale. No element is created, removed or reordered.
   Writes are guarded so a no-op size change touches the DOM zero times. */
function refreshActiveCardDpi(){
  var art=getActive(); if(!art||!slotsRow)return;
  var el=slotsRow.querySelector('.dgs-slot.is-active .dgs-slot-dpi');
  if(!el)return;
  var dpi=calcDPI(art),q=dpiQuality(dpi),txt=dpiLabel(dpi),cls='dgs-slot-dpi '+q.cls;
  if(el.textContent!==txt)el.textContent=txt;
  if(el.className!==cls)el.className=cls;
}

function refreshBadges(){
  var art=getActive();
  /* DPI badge */
  if(!art){
    dpiBadge.className='dgs-badge neutral';
    dpiBadge.textContent='Upload artwork to check DPI';
  } else {
    var dpi=calcDPI(art);
    /* Status leads, DPI number follows as supporting detail. Thresholds, the
       className (and therefore the semantic colour) and the title text are all
       unchanged — only the order of the words in the label. */
    /* Visible label stays one line: status first, DPI as supporting detail.
       The plain-English explanation lives in title= — the help mechanism this
       file already uses everywhere else — so nothing extra is added to the
       workspace. GOOD previously had NO tooltip at all; it now explains why it
       is fine, which is the reassurance half of the feature.
       Thresholds, className (and therefore the semantic colour) and calcDPI are
       all untouched — only the words change. */
    var q=dpiQuality(dpi);
    var note=q.cls==='okay'?' — May print soft':q.cls==='low'?' — May print blurry':'';
    var tip=q.cls==='good'?'Artwork resolution is suitable for this print size.'
           :q.cls==='okay'?'Some fine detail may print slightly soft.'
           :'Artwork may look blurry at this print size.';
    dpiBadge.className='dgs-badge '+q.cls;
    /* The consequence (" — May print soft/blurry") rides in its own span so the
       mobile stylesheet can hide it and keep the pill compact — on mobile the
       consequence already appears in the stage tip (LOW/OKAY outrank everything
       there), so nothing is lost. Desktop renders character-identical text.
       Static strings only — no user content passes through this innerHTML. */
    dpiBadge.innerHTML=dpiLabel(dpi)+(note?'<span class="dgs-dpi-note">'+note+'</span>':'');
    dpiBadge.title=tip;
  }
  /* size badge */
  /* toolbar only — space before the unit. The Design Summary and the exported
     preview sheet keep their own formatting and are deliberately untouched. */
  sizeBadge.textContent=S.widthMm+' × '+S.heightMm+' mm';
  /* shape badge */
  /* Toolbar-only label. SHAPE_LABELS is ALSO consumed by the Design Summary and
     twice by the exported preview sheet, where "Shape: Custom" already reads
     correctly — so it must not change. Only 'custom' is ambiguous on its own;
     Circle/Square/Rectangle are already self-explanatory and fall through. */
  shapeBadge.textContent=TOOLBAR_SHAPE_LABELS[S.shape]||SHAPE_LABELS[S.shape]||S.shape;
  /* complexity warning — custom die-cut only */
  var complexBadge=root.querySelector('[data-dgs-complex-badge]');
  if(complexBadge)complexBadge.style.display=(art&&S.shape==='custom'&&art.complex)?'inline-flex':'none';
  /* ── stage tip: single highest-priority message ──────────────────────────
     One element, one assignment — structurally incapable of showing two tips,
     rotating or queueing.

     Priority is deliberately QUALITY FIRST. The previous chain tested shape
     before artwork condition, so on circle/square/rectangle a print-quality or
     background warning could never surface at all.

     Division of labour: the artwork card describes the FILE (Transparent PNG /
     Background removed / Solid background); the stage tip states the
     CONSEQUENCE for the current setup. So the tip never simply restates the
     card — the transparency branches only appear for Custom shape, where they
     change what actually gets cut.

     Reuses calcDPI() and dpiQuality() — no duplicated thresholds. */
  var tipQ=art?dpiQuality(calcDPI(art)).cls:null;
  var tipWarn=false;
  if(uploadError){
    /* A failed upload outranks every other message: it is the only one telling
       the customer that something THEY did has not worked. Placed at the head
       of the existing single-assignment chain, so it cannot coexist with any
       other tip, and re-applied on every refresh so a later render cannot
       silently swallow it. In the empty state this element is hidden by
       .dgs.is-empty and the upload card carries the message instead. */
    stageTip.textContent=uploadError;
    tipWarn=true;
  } else if(!art){
    /* hidden by .dgs.is-empty in CSS; kept so the element is never empty */
    stageTip.textContent='Cutline is a visual overlay only. Final cutlines are checked manually before print.';
  } else if(tipQ==='low'){
    stageTip.textContent='This artwork may print blurry at this size.';
  } else if(tipQ==='okay'){
    stageTip.textContent='This artwork may print soft at this size.';
  } else if(S.shape==='custom'&&art.wasWhiteRemoved){
    stageTip.textContent='White background removed automatically.';
  } else if(S.shape==='custom'&&art.hasTransparency){
    /* hasTransparency is the engine's OWN test for "there is a usable subject
       edge" — true for real alpha and for successfully removed backgrounds, and
       exactly what decides whether the cutline traces the artwork. Promising a
       custom cut is only honest inside this branch. */
    stageTip.textContent='Your sticker will be cut around your artwork.';
  } else if(S.shape==='custom'){
    /* Custom + no usable transparency (photo / screenshot / flattened JPG that
       white-removal could not separate). analyseArtwork sets usedBlob and the
       cutline comes from buildBlobContour() — a smoothed sin/cos outline, NOT a
       trace of the artwork. Real mobile QA showed customers proceeding here
       without realising that, so this is a genuine WARNING (amber .is-warning
       variant), not the old neutral "rounded shape" note. hasTransparency is
       the engine's own authoritative separability flag — true for real alpha
       AND for the approved white-removal path — so no new analysis is added. */
    /* copy shortened for mobile after real-phone QA; the CSS warning variant
       suppresses the "TIP:" prefix so this renders as "⚠ CUSTOM SHAPE: …" */
    stageTip.textContent='CUSTOM SHAPE: This artwork has a solid background. Use Square or Rectangle, or upload a transparent PNG.';
    tipWarn=true;
  } else if(S.shape==='circle'){
    stageTip.textContent='Your artwork will be fitted inside a circle.';
  } else if(S.shape==='square'){
    stageTip.textContent='Your artwork will be fitted inside a square.';
  } else {
    stageTip.textContent='Your artwork will be fitted inside a rectangle.';
  }
  /* The amber .is-warning variant is live again for exactly ONE message — the
     Custom + solid-background warning above. The toggle clears the class on
     every other branch, so it can never stick after switching shape or artwork. */
  var tipBox=stageTip.parentNode;
  if(tipBox&&tipBox.classList)tipBox.classList.toggle('is-warning',tipWarn);
}

function refreshSummary(){
  var art=getActive();
  sumFile.textContent=art?art.name:'No artwork selected';
  /* full name stays recoverable now that the value truncates with an ellipsis */
  sumFile.title=art?art.name:'';
  sumShape.textContent=SHAPE_LABELS[S.shape]||S.shape;
  sumSize.textContent=S.widthMm+' × '+S.heightMm+'mm';
  sumBorder.textContent=S.borderMm===0?'None (Flush Cut)':S.borderMm+'mm';
  if(sumBorderCol)sumBorderCol.textContent=S.borderMm===0?'None':colourName(S.borderColour);
  if(sumBg)sumBg.textContent=(!S.fillColour||S.fillColour==='none')?'None (transparent)':colourName(S.fillColour);
  if(!art){sumDpi.textContent='—';}
  else{
    /* Same rendered text as before — only the quality WORD is now wrapped so it
       can carry the existing semantic colour while the DPI figure stays neutral.
       Derives the bucket from dpiQuality(), which also removes one of the
       duplicated threshold ternaries. No content added or removed. */
    var dpi=calcDPI(art), q=dpiQuality(dpi);
    var word=q.cls==='good'?'Good':q.cls==='okay'?'Okay':'Low';
    var note=q.cls==='good'?'':q.cls==='okay'?' (may print soft)':' (may print blurry)';
    sumDpi.innerHTML=dpi+' DPI · <span class="dgs-sum-dpi-q '+q.cls+'">'+word+'</span>'+note;
  }
}

function colourName(hex){
  if(hex==='#ffffff'||hex==='#FFFFFF')return 'White';
  if(hex==='#111111'||hex==='#000000')return 'Black';
  return hex.toUpperCase();
}

/* ═══════════════════════════════════════════════════════════════════
   DIMENSIONS
═══════════════════════════════════════════════════════════════════ */
/* Custom longest-edge is limited to 40–100mm for all fixed-ratio shapes. Applied
   on every read so loaded/seeded/typed values can never display out of range. */
function clampCustomEdge(v){v=Math.round(v)||75;return v<40?40:(v>100?100:v);}

function computeDimensions(){
  var art=getActive();
  if(S.sizePreset==='custom'){
    if(S.shape!=='rectangle'){
      /* Fixed-ratio shapes (Custom / Circle / Square): ONE editable value,
         S.customEdge (40–100mm). Custom derives the second dimension from the
         artwork aspect; Circle (diameter) and Square (side) are 1:1. Geometry is
         unchanged — the preview already uses art.outlineAspect / a 1:1 box. */
      var ce=clampCustomEdge(S.customEdge);
      S.customEdge=ce;
      S.longestEdgeMm=ce;
      if(S.shape==='custom'){
        var car=art?(art.outlineAspect||1):1;
        if(car>=1){S.widthMm=ce;S.heightMm=Math.round(ce/car);}   /* landscape/square: width is longest */
        else{S.heightMm=ce;S.widthMm=Math.round(ce*car);}          /* portrait: height is longest */
      } else {
        S.widthMm=ce;S.heightMm=ce;                                /* circle = diameter, square = side */
      }
      return;
    }
    /* Rectangle keeps independent Width × Height (unchanged behaviour). */
    S.widthMm=S.customW||75;
    S.heightMm=S.customH||75;
    S.longestEdgeMm=Math.max(S.widthMm,S.heightMm);
    S.rectW=S.widthMm;S.rectH=S.heightMm;   /* keep rectangle reference dims in sync */
    return;
  }
  var longest=parseInt(S.sizePreset,10)||75;
  S.longestEdgeMm=longest;
  var ar;
  if(S.shape==='circle'||S.shape==='square'){ar=1;}
  else if(S.shape==='rectangle'){
    /* match the artwork's own proportions (visible content) when present */
    ar=art?(art.outlineAspect||S.rectW/(S.rectH||1)):(S.rectW/(S.rectH||1));
  }
  else{ar=art?art.outlineAspect||1:1;}
  if(ar>=1){S.widthMm=longest;S.heightMm=Math.round(longest/ar);}
  else{S.heightMm=longest;S.widthMm=Math.round(longest*ar);}
}

/* Show/hide the custom-size inputs. The whole wrap is visible only for the
   Custom size preset. Within it, every FIXED-RATIO shape (Custom / Circle /
   Square) shows the single aspect-locked "Longest Edge" field; only Rectangle
   shows the independent W × H grid. */
function refreshCustomSizeUI(){
  if(!customSizeWrap)return;
  var isCustomSize=(S.sizePreset==='custom');
  customSizeWrap.style.display=isCustomSize?'block':'none';
  var lockEdge=isCustomSize&&S.shape!=='rectangle';   /* custom/circle/square = one input */
  if(customEdgeWrap)customEdgeWrap.style.display=lockEdge?'block':'none';
  if(customWhWrap)customWhWrap.style.display=lockEdge?'none':'grid';
  if(customEdge)customEdge.value=S.customEdge;
  if(customW)customW.value=S.customW;
  if(customH)customH.value=S.customH;
}

/* ═══════════════════════════════════════════════════════════════════
   COLOUR CONTROLS — Border Colour & Background Colour share the same
   None / White / Black / Custom logic, editable hex, per-artwork storage.
═══════════════════════════════════════════════════════════════════ */
/* Validate + normalise a hex string → '#rrggbb' lowercase, or null. */
function normHex(v){
  if(!v)return null;
  v=String(v).trim();if(v[0]!=='#')v='#'+v;
  if(/^#[0-9a-fA-F]{3}$/.test(v))v='#'+v[1]+v[1]+v[2]+v[2]+v[3]+v[3];
  return /^#[0-9a-fA-F]{6}$/.test(v)?v.toLowerCase():null;
}
function whichColourChip(col){
  col=(col||'').toLowerCase();
  /* treat legacy #111111 as black too */
  if(col==='#ffffff')return '#ffffff';
  if(col==='#000000'||col==='#111111')return '#000000';
  return 'custom';
}
/* Open the OS colour picker for a <input type=color> (showPicker, then click). */
function openNativePicker(el){
  if(!el)return;
  try{el.focus();}catch(e){}
  if(typeof el.showPicker==='function'){try{el.showPicker();return;}catch(e){}}
  try{el.click();}catch(e){}
}

/* UI-only "custom mode" latches (NOT persisted). They keep Custom selected even
   when the chosen colour happens to equal white/black. Derived from the stored
   colour whenever an artwork loads (see refreshColourModes). */
var borderCustomMode=false, fillCustomMode=false;
/* Session memory of the last CUSTOM colour the customer chose, so re-selecting
   Custom restores it instead of resetting. null until they pick one (first time
   defaults to the brand colour). */
var lastBorderCustom=null, lastFillCustom=null;
function refreshColourModes(){
  borderCustomMode=(whichColourChip(S.borderColour)==='custom');
  fillCustomMode=(S.fillColour!=='none'&&whichColourChip(S.fillColour)==='custom');
}
/* Active chip for each control: none / white / black / custom. */
function borderWhich(){
  if(S.borderMm===0)return 'none';
  var c=whichColourChip(S.borderColour);
  return (c==='custom'||borderCustomMode)?'custom':c;
}
function fillWhich(){
  if(!S.fillColour||S.fillColour==='none')return 'none';
  var c=whichColourChip(S.fillColour);
  return (c==='custom'||fillCustomMode)?'custom':c;
}

/* ── Border colour ── (swatch + hex row shows ONLY in Custom mode) */
function syncBorderColourUI(){
  var col=(S.borderColour||'#ffffff').toLowerCase();
  var which=borderWhich();
  root.querySelectorAll('[data-dgs-bordercol]').forEach(function(b){
    b.classList.toggle('active',b.getAttribute('data-dgs-bordercol')===which);
  });
  if(borderRow)borderRow.style.display=(which==='custom')?'grid':'none';
  if(borderPicker)borderPicker.value=col;                 /* swatch = live preview */
  if(borderHexInput&&document.activeElement!==borderHexInput){borderHexInput.value=col.toUpperCase();borderHexInput.classList.remove('invalid');}
}
function setColour(hex){
  var h=normHex(hex);if(!h)return;
  S.borderColour=h;
  if(whichColourChip(h)==='custom')lastBorderCustom=h;   /* remember last custom colour */
  syncBorderColourUI();
  commitActive();refreshSummary();
  /* ensureBorderOn() may have just lifted a 0mm border to 2/2.5mm, and the hex
     input writes borderSlider.value before calling here — this one call covers
     both, plus the White/Black/Custom chips, without a new listener. */
  dgsSyncSliderFill();
  /* Border WIDTH is what changed here — every caller runs ensureBorderOn()
     first, which can lift a 0mm border to 2/2.5mm. On a geometric shape the
     artwork is contain-fitted inside fitW-2*borderPx, so a wider border makes
     the artwork physically smaller and its effective DPI higher. The summary
     was already repainted above; the pill and the artwork card were not, and
     would otherwise disagree with it. Colour alone does not affect DPI — this
     is simply the one place every border-width change funnels through. */
  refreshBadges();refreshActiveCardDpi();
}
/* enable the border (give it a width) if it is currently off */
function ensureBorderOn(){
  if(S.borderMm===0){
    S.borderMm=(S.shape==='custom')?2:2.5;
    borderSlider.value=String(S.borderMm);borderVal.textContent=S.borderMm+'mm';
    root.querySelectorAll('[data-dgs-border-preset]').forEach(function(b){b.classList.toggle('active',parseFloat(b.getAttribute('data-dgs-border-preset'))===S.borderMm);});
  }
}
/* chip click: none = border off (blocked for custom); white/black = preset;
   custom = latch custom, enable border, open the OS colour picker immediately. */
function chooseBorderMode(val){
  if(val==='none'){
    if(S.shape==='custom')return;            /* custom needs a border */
    borderCustomMode=false;
    S.borderMm=0;borderSlider.value='0';borderVal.textContent='0mm';
    root.querySelectorAll('[data-dgs-border-preset]').forEach(function(b){b.classList.toggle('active',parseFloat(b.getAttribute('data-dgs-border-preset'))===0);});
    syncBorderColourUI();commitActive();refreshSummary();
    /* border width -> 0 changes effective DPI (see setColour) */
    refreshBadges();refreshActiveCardDpi();
    dgsSyncSliderFill();   /* slider was just driven to 0 */
    return;
  }
  ensureBorderOn();
  if(val==='custom'){
    borderCustomMode=true;
    /* First time → brand orange; afterwards → the customer's remembered custom colour. */
    setColour(lastBorderCustom||TOK.orange);
    openNativePicker(borderPicker);
    return;
  }
  borderCustomMode=false;
  setColour(val);                            /* white / black */
}

/* ── Background colour ── (swatch + hex row shows ONLY in Custom mode) */
function syncFillUI(){
  var fv=S.fillColour||'none',which=fillWhich();
  root.querySelectorAll('[data-dgs-fill]').forEach(function(b){
    b.classList.toggle('active',b.getAttribute('data-dgs-fill')===which);
  });
  if(fillRow)fillRow.style.display=(which==='custom')?'grid':'none';
  if(fv!=='none'){
    if(fillPicker)fillPicker.value=fv;                    /* swatch = live preview */
    if(fillHexInput&&document.activeElement!==fillHexInput){fillHexInput.value=fv.toUpperCase();fillHexInput.classList.remove('invalid');}
  }
}
function setFill(val){
  if(val!=='none'){var h=normHex(val);if(!h)return;val=h;}
  S.fillColour=val;
  if(val!=='none'&&whichColourChip(val)==='custom')lastFillCustom=val;   /* remember last custom */
  syncFillUI();
  commitActive();refreshSummary();
}
function chooseFillMode(val){
  if(val==='custom'){
    fillCustomMode=true;
    /* First time → brand yellow; afterwards → the customer's remembered custom colour. */
    setFill(lastFillCustom||TOK.yellow);
    openNativePicker(fillPicker);
    return;
  }
  fillCustomMode=false;
  setFill(val);                              /* none / white / black */
}

/* ═══════════════════════════════════════════════════════════════════
   FILE LOADING
═══════════════════════════════════════════════════════════════════ */
/* ── upload failure reporting ─────────────────────────────────────────────
   A FileReader error, or an image the browser cannot decode, used to end the
   upload in silence: no slot appeared, nothing was said, and with DGS_DEBUG
   false nothing reached the console either. The customer's only signal was
   that clicking Upload did nothing.

   Reported through surfaces the studio ALREADY owns, and deliberately through
   whichever one is actually visible at the time — the two states are mutually
   exclusive, so exactly one message is ever on screen:
     EMPTY state     the upload card is showing; its kicker is already the
                     card's amber line, so it carries the headline and the sub
                     line beneath it carries the guidance. No new element, no
                     new CSS, no layout change.
     POPULATED state the upload card is hidden and the stage tip is showing, so
                     the message goes there through the existing .is-warning
                     amber variant (which already swaps the "TIP:" prefix for
                     the warning glyph).
   No alert(), and valid PNG/JPG uploads are completely untouched.

   Cleared when new files are chosen. A failure is deliberately NOT cleared by
   a later success in the same batch: if one of five files failed, that is worth
   saying, and refreshBadges() re-applies it so a re-render cannot swallow it. */
var uploadKicker = uploadOverlay?uploadOverlay.querySelector('.dgs-upload-overlay-kicker'):null;
var uploadSub    = uploadOverlay?uploadOverlay.querySelector('.dgs-upload-overlay-sub'):null;
var UPLOAD_KICKER_DEFAULT = uploadKicker?uploadKicker.textContent:'';
var UPLOAD_SUB_DEFAULT    = uploadSub?uploadSub.textContent:'';
var UPLOAD_ERROR_KICKER   = 'Could not load that file';
var UPLOAD_ERROR_TEXT     = 'That file could not be opened. Please try another PNG or JPG.';
var uploadError = null;
function setUploadError(on){
  uploadError = on?UPLOAD_ERROR_TEXT:null;
  if(uploadKicker)uploadKicker.textContent = uploadError?UPLOAD_ERROR_KICKER:UPLOAD_KICKER_DEFAULT;
  if(uploadSub)   uploadSub.textContent    = uploadError||UPLOAD_SUB_DEFAULT;
  refreshBadges();   /* re-runs the stage-tip priority chain */
}

function loadFiles(fileList){
  var files=Array.from(fileList||[]);
  setUploadError(false);   /* a new selection clears any previous failure */
  var remaining=MAX_SLOTS-S.artworks.length;
  if(!remaining)return;
  files.slice(0,remaining).forEach(function(file){
    var ok=/^image\/(png|jpeg|jpg)$/i.test(file.type)||/\.(png|jpe?g)$/i.test(file.name);
    if(!ok)return;
    var reader=new FileReader();
    reader.onerror=function(){setUploadError(true);};
    reader.onload=function(e){
      var img=new Image();
      img.onerror=function(){setUploadError(true);};
      img.onload=function(){
        /* Slot capacity is re-checked HERE, not only where the files were
           chosen: `remaining` above is read synchronously while every push
           happens in this async callback, so two quick selections (or a drop
           landing mid-decode) could both see the same free slots and overshoot
           MAX_SLOTS. Checked before analysis so a discarded file costs nothing. */
        if(S.artworks.length>=MAX_SLOTS)return;
        /* New uploads always analyse at default smoothing so their stored
           contour matches their default per-artwork settings. */
        var analysis=analyseArtwork(img,defaultDesign().smoothPasses);
        var art={
          id:'art-'+Date.now()+'-'+Math.random().toString(36).slice(2,7),
          name:file.name,
          url:e.target.result,
          img:img,
          outlinePoints:analysis.outlinePoints,
          outlineAspect:analysis.outlineAspect,
          hasTransparency:analysis.hasTransparency,
          wasWhiteRemoved:analysis.wasWhiteRemoved,
          cropRect:analysis.cropRect,
          maskW:analysis.maskW,
          maskH:analysis.maskH,
          mask:analysis.cleanMask,
          cutMask:analysis.cutMask,
          cutW:analysis.cutW,
          cutH:analysis.cutH,
          complex:analysis.complex,
          dominantColours:analysis.dominantColours,
          settings:defaultDesign()   /* each artwork starts with its OWN defaults */
        };
        /* Auto-pick the most sensible default shape from the analysis. */
        art.autoShape=autoDefaultShape(art);
        art.settings.shape=art.autoShape;
        S.artworks.push(art);
        /* A real artwork now exists — this is the moment a project begins. */
        dgsMarkProjectChanged();
        if(!S.activeId){
          activateArtwork(art.id);   /* first upload becomes active (its own defaults) */
        } else {
          commitActive();            /* preserve current active artwork's edits */
          refreshSlots();            /* just show the new slot; don't switch active */
        }
      };
      img.src=e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

/* ═══════════════════════════════════════════════════════════════════
   EXPORT
═══════════════════════════════════════════════════════════════════ */
/* Sanitise an uploaded filename for use in download names: drop the extension,
   replace spaces and invalid characters with hyphens, collapse repeats. */
function baseName(name){
  var b=(name||'artwork').replace(/\.[^.]+$/,'')
    .replace(/[^a-zA-Z0-9._-]+/g,'-')
    .replace(/-+/g,'-').replace(/^[-.]+|[-.]+$/g,'');
  return b||'artwork';
}

/* ── small canvas helpers (export only) ───────────────────────────────── */
function roundRectPath(c,x,y,w,h,r){
  c.beginPath();
  c.moveTo(x+r,y);
  c.arcTo(x+w,y,x+w,y+h,r);
  c.arcTo(x+w,y+h,x,y+h,r);
  c.arcTo(x,y+h,x,y,r);
  c.arcTo(x,y,x+w,y,r);
  c.closePath();
}
function wrapText(c,text,x,y,maxW,lh){
  var words=String(text).split(' '),line='';
  for(var n=0;n<words.length;n++){
    var test=line?line+' '+words[n]:words[n];
    if(c.measureText(test).width>maxW&&line){c.fillText(line,x,y);line=words[n];y+=lh;}
    else line=test;
  }
  if(line)c.fillText(line,x,y);
  return y+lh;
}

/* PREVIEW IMAGE — a branded, customer-facing reference SHEET (not a proof).
   Reuses render(0) for the sticker (with its dashed cutline) and composites it
   onto a portrait sheet with branding, a specification table, a reassurance
   note and a footer. Layout/typography only — the preview PANEL below (grid +
   sticker) is untouched from the previous version: same position, same size,
   same drawImage call, so the rendered artwork/cutline is pixel-identical. */
function buildPreviewSheet(){
  var art=getActive();if(!art)return null;
  render(0,{sheen:false});           /* static sticker, NO sheen, for the exported sheet */
  var sticker=canvasEl;

  var FF=TOK.bodyFont, FFH=TOK.headFont;
  var W=1080,H=1620,M=64;
  /* palette now inherited from the shared design tokens — no second copy to drift */
  var BG=TOK.bg,TEXT=TOK.text,MUTED=TOK.muted,LINE=TOK.border,YELLOW=TOK.yellow;
  var PANEL2=TOK.panel,GOOD=TOK.success,OKAY=TOK.warning,LOW=TOK.error;
  var c=document.createElement('canvas');c.width=W;c.height=H;
  var x=c.getContext('2d');
  /* The brand PNGs are ~3.3x larger than their drawn size, so the default
     smoothing hint was producing visibly choppy edges on the downscale. Enable
     high-quality resampling once for the whole sheet; every drawImage below
     draws ONCE, at final integer dimensions derived from the source's natural
     size, so nothing is ever rescaled twice or landed on fractional pixels.
     Rendering hint only — no filters, no sharpening, no geometry change. */
  try{ x.imageSmoothingEnabled=true; x.imageSmoothingQuality='high'; }catch(e){}
  x.fillStyle=BG;x.fillRect(0,0,W,H);

  /* shared brand gradient (orange→yellow) — the same accent used throughout the
     live app for active states, rings and the cutline */
  function brandGradient(x0,y0,x1,y1){
    var g=x.createLinearGradient(x0,y0,x1,y1);
    g.addColorStop(0,TOK.yellow);g.addColorStop(1,TOK.orange);
    return g;
  }

  /* ── header: brand mark + preview title ──────────────────────────────
     Brand mark is the real DeadGood logo when it's ready (checked
     synchronously — never waited on), otherwise a refined text wordmark with
     a small brand-gradient accent rule. Either way the title sits at the same
     fixed position, so the layout never shifts depending on image load. */
  x.textAlign='center';
  var logoReady=false;
  try{ logoReady=!!(dgsSheetLogoImg && dgsSheetLogoImg.complete && dgsSheetLogoImg.naturalWidth>0); }catch(e){}
  if(logoReady){
    try{
      /* Now the URL-free wordmark (1231×187, inspected: zero built-in
         transparent padding). Sized HEIGHT-first at 56px, because cap height —
         not overall width — is what sets a text logo's visual weight, and the
         two assets have different aspects. Width follows from the natural
         ratio, so the mark can never stretch; the 520px width clamp only ever
         scales DOWN. All four drawImage args are rounded to integers so the
         downscale lands on whole pixels. Drawn at y=36 (bottom ~92) so logo and
         title cap (~124) read as one header group; panel still starts at 176. */
      var ratio=dgsSheetLogoImg.naturalWidth/dgsSheetLogoImg.naturalHeight;
      var logoH=56,logoW=ratio*logoH;
      if(logoW>520){logoW=520;logoH=logoW/ratio;}
      logoW=Math.round(logoW);logoH=Math.round(logoH);
      x.drawImage(dgsSheetLogoImg,Math.round((W-logoW)/2),36,logoW,logoH);
    }catch(e){ logoReady=false; }
  }
  if(!logoReady){
    x.fillStyle=MUTED;x.font='800 12px '+FF;
    x.fillText('D E A D G O O D   S T I C K E R S',W/2,54);
    x.fillStyle=brandGradient(W/2-22,0,W/2+22,0);
    x.fillRect(W/2-22,66,44,3);
  }
  /* sheet title uses the theme HEADING face at its real weight (Poetsen One ships
     only 400 — forcing 800 here would synthesise a faux bold). */
  /* 28 -> 34px (+21%): reads as the page's primary heading instead of a caption.
     Baseline 152 -> 148 tightens the logo-to-title pairing; with descenders
     ending ~157 the preview panel's y=176 top edge keeps ~19px of clear air, so
     the hero is not crowded. ONE fixed baseline for both the image and fallback
     paths, so the layout never shifts on logo availability. */
  x.fillStyle=TEXT;x.font='400 34px '+FFH;
  x.fillText('Your Sticker Preview',W/2,148);

  /* preview area — UNCHANGED from the previous build: same position/size, same
     grid background matching the on-screen grid mode, same sticker drawImage
     call (cutline included). ONLY this panel changes with gridLight; the rest
     of the proof stays dark/branded. Print PNG never reads gridLight. */
  var pax=M,pay=176,pw=W-2*M,ph=pw;       /* square preview panel */
  var panelBg=gridLight?'#d1d1d1':TOK.canvas;
  var panelGrid=gridLight?'rgba(0,0,0,.08)':'rgba(255,255,255,.065)';
  roundRectPath(x,pax,pay,pw,ph,18);x.fillStyle=panelBg;x.fill();
  x.save();roundRectPath(x,pax,pay,pw,ph,18);x.clip();
  x.strokeStyle=panelGrid;x.lineWidth=1;
  for(var gx=pax;gx<=pax+pw;gx+=40){x.beginPath();x.moveTo(gx,pay);x.lineTo(gx,pay+ph);x.stroke();}
  for(var gy=pay;gy<=pay+ph;gy+=40){x.beginPath();x.moveTo(pax,gy);x.lineTo(pax+pw,gy);x.stroke();}
  /* 30 -> 24 lets the artwork fill 904 of the 952 panel instead of 892. Still a
     generous inset, so the cutline never crowds the panel edge. Source rect is
     untouched (0,0,CANVAS_RES,CANVAS_RES) — this scales the destination only,
     so rendering and export quality are unchanged. */
  var sPad=24,avail=pw-2*sPad,sw=avail,sh=avail;
  x.drawImage(sticker,0,0,CANVAS_RES,CANVAS_RES,pax+(pw-sw)/2,pay+(ph-sh)/2,sw,sh);
  x.restore();
  roundRectPath(x,pax,pay,pw,ph,18);x.strokeStyle=LINE;x.lineWidth=1;x.stroke();

  /* ── sticker specification: six individual cards (2 columns × 3 rows) ──
     Same visual language as the app's own cards (panel2 fill, thin border,
     16px radius, no glow) instead of one large table — reads as a set of
     discrete, scannable facts. Six items only: nothing customer-identifying
     (filename/date/order/customer) is drawn here. */
  var dpi=calcDPI(art);
  var dpiStatus=dpi>=300?'good':dpi>=150?'okay':'low';
  var dpiColour=dpiStatus==='good'?GOOD:dpiStatus==='okay'?OKAY:LOW;
  var dpiTxt=dpi+(dpiStatus==='good'?' DPI · Good':dpiStatus==='okay'?' DPI · Okay':' DPI · Low');
  var stickerTypeMap={custom:'Custom Die Cut',circle:'Circle',square:'Square',rectangle:'Rectangle'};
  var stickerType=stickerTypeMap[S.shape]||SHAPE_LABELS[S.shape]||S.shape;

  /* Baseline pay+ph+40 -> +50 (+10px): the heading sat ~21px below the preview
     panel, which read as slightly tethered to it. At 26px the cap is ~19px, so
     this lifts the clear air between panel bottom and heading cap to ~31px and
     the whole specification block — heading, cards, disclaimer — shifts down
     together, since every offset below is derived from specTop.
     Heading size, colour, weight, uppercase and centring all unchanged. */
  var specTop=pay+ph+50;
  x.textAlign='center';x.fillStyle=YELLOW;x.font='800 26px '+FF;
  x.fillText('STICKER SPECIFICATION',W/2,specTop);

  /* cardH 76 -> 70. Three rows means this returns 18px to the bottom of the
     sheet, where the reassurance note was clearing the footer rule by only ~5px. */
  /* cardH 70 -> 48: with label and value sharing one baseline the card no longer
     needs to hold a two-row stack, which reclaims 66px across the three rows. */
  /* Card height 48 -> 53 and the row gap split out from the column gap at 15px
     (was a single 12px value used for both). Widths, the 2x3 structure, the
     16px radius, the 16px internal padding and the 12px COLUMN gap are all
     unchanged — cardW still derives from cardGap, so the columns are untouched.
     The extra 5px of height plus 3px of row rhythm let the enlarged label/value
     pair breathe and lets the block hold its share of the page without becoming
     chunky. Net growth: 3x5 height + 2x3 gap = 21px. */
  var gridTop=specTop+30,cardGap=12,rowGap=15,cardW=(pw-cardGap)/2,cardH=53,cardPad=16;
  var col1X=pax,col2X=pax+cardW+cardGap;
  var row1Y=gridTop,row2Y=row1Y+cardH+rowGap,row3Y=row2Y+cardH+rowGap;
  function specCard(cx,cy,label,value,valueColour){
    roundRectPath(x,cx,cy,cardW,cardH,16);x.fillStyle=PANEL2;x.fill();
    x.strokeStyle=LINE;x.lineWidth=1;x.stroke();
    x.textAlign='left';
    /* Single-row layout: label pinned left, value pinned right, both on ONE
       shared baseline. This is the same construction the app's Design Summary
       already uses (label left / value right / align-items:baseline), so the
       export reads as part of the same system.
       The 470px card previously carried a stacked pair using ~150px of width —
       going horizontal spends width that was already paid for and removes a
       whole text row from the height.
       Weights and colours are unchanged: label 700 MUTED (secondary), value 800
       in TEXT or its semantic DPI colour (dominant). */
    /* Typography refresh, card geometry untouched (470×48, pad 16, radius 16):
       label 14 -> 16px so the label sits at ~76% of the 21px value — inside the
       requested 70–75% band once uppercase optical size is accounted for — and
       no longer reads as tiny metadata. Value stays 21px (already right), still
       800 weight in TEXT or its semantic DPI colour, so the value remains
       unambiguously primary. baseY 31 keeps the shared baseline optically
       centred in the 48px card (21px cap ~15px from the top, 17px below the
       baseline). Worst-case widths re-measured at the new sizes: label
       'ARTWORK QUALITY' (~164px) + value '1158 DPI · Good' (~176px) total
       ~340px of the 438px inner width — no collision possible.
       baseY 31 -> 34 for the 53px card: the 21px value's cap (~15px) now sits
       19px from the card top with 19px below the baseline — exactly centred. */
    var baseY=cy+34;
    x.textAlign='left';
    x.fillStyle=MUTED;x.font='700 16px '+FF;
    x.fillText(label.toUpperCase(),cx+cardPad,baseY);
    x.textAlign='right';
    x.fillStyle=valueColour||TEXT;x.font='800 21px '+FF;
    x.fillText(value,cx+cardW-cardPad,baseY);
    x.textAlign='left';
  }
  specCard(col1X,row1Y,'Shape',SHAPE_LABELS[S.shape]||S.shape);
  specCard(col2X,row1Y,'Finished Size',S.widthMm+' × '+S.heightMm+' mm');
  specCard(col1X,row2Y,'Border Width',S.borderMm===0?'None':S.borderMm+' mm');
  specCard(col2X,row2Y,'Border Colour',S.borderMm===0?'None':colourName(S.borderColour));
  specCard(col1X,row3Y,'Artwork Quality',dpiTxt,dpiColour);
  specCard(col2X,row3Y,'Sticker Type',stickerType);

  var gridBottom=row3Y+cardH;

  /* ── reassurance note ──────────────────────────────────────────────── */
  /* 19px/29 -> 21px/30, and the copy is now drawn as TWO logical sections with
     an EXPLICIT break: sentence one is a single direct fillText call, so it is
     structurally guaranteed its own line at any font metric, then sentence two
     wraps naturally at maxWidth 700 into two visually balanced lines — three
     deliberate lines total instead of arbitrary wrapping. Wording unchanged.
     Still MUTED: customer reassurance, not fine print, and phone-readable.
     Width 700 -> 610. Counter-intuitive but measured: wrapping is greedy, so a
     WIDER measure packs more onto line 2 and leaves line 3 shorter. Widths of
     sentence two's two lines, measured at 21px:
        610 -> 588/488 (spread 100)   <- chosen
        650 -> 639/437 (spread 202)
        700 -> 677/399 (spread 278)   <- previous
        740 -> 677/399 (spread 278)   (same break; widening changed nothing)
        760 -> 741/336 (spread 405)
     610 is the narrowest measure that still holds sentence two on exactly two
     lines, and gives the most balanced pair. With sentence one at 633px the
     block reads 633 / 588 / 488 — a clean taper instead of a stranded stub.
     First baseline gridBottom+49, recomputed for the taller card grid: at 21px
     the cap is ~15px, so this puts ~34px of clear air between the last card row
     and the top of the disclaimer — inside the intended 32-40px band — and the
     three-line block still clears the footer rule comfortably. */
  x.textAlign='center';x.fillStyle=MUTED;x.font='400 21px '+FF;
  x.fillText('This preview has been generated using your current artwork and settings.',W/2,gridBottom+49);
  wrapText(x,'Every order is manually checked before production, and a final digital proof will always be emailed for approval before printing.',W/2,gridBottom+79,610,30);

  /* ── footer: thin divider + website branding asset ───────────────────── */
  var footRuleY=H-88;
  x.strokeStyle=LINE;x.lineWidth=1;
  x.beginPath();x.moveTo(M,footRuleY);x.lineTo(W-M,footRuleY);x.stroke();
  /* The supplied .co.uk branding asset (1231×187, no built-in padding) replaces
     the drawn gradient text — image ONLY, never both, so the address is never
     duplicated. Width-first at 310px -> ~47px tall at its natural 6.58:1
     aspect (height-clamped as a swap-safety net; both constraints only scale
     DOWN), vertically centred in the 88px footer band below the rule.
     If the CDN asset has not loaded, the previous drawn treatment is the
     fallback so the sheet always carries the address. */
  var footReady=false;
  try{ footReady=!!(dgsSheetFooterImg && dgsSheetFooterImg.complete && dgsSheetFooterImg.naturalWidth>0); }catch(e){}
  if(footReady){
    try{
      /* Sized HEIGHT-first like the header, for the same reason: cap height is
         what carries visual weight across two assets of different aspect.
         50px tall vs the previous footer mark's 47px is ~6% more substantial —
         a clear closing signature — while staying below the header's 56px so it
         never competes with it. Width follows the natural 8.85:1 ratio (clamped
         DOWN only), all args integer, and the mark is vertically centred in the
         88px band below the divider with ~19px clear top and bottom. */
      var fRatio=dgsSheetFooterImg.naturalWidth/dgsSheetFooterImg.naturalHeight;
      var fH=50,fW=fRatio*fH;
      if(fW>560){fW=560;fH=fW/fRatio;}
      fW=Math.round(fW);fH=Math.round(fH);
      x.drawImage(dgsSheetFooterImg,Math.round((W-fW)/2),Math.round(footRuleY+(88-fH)/2),fW,fH);
    }catch(e){ footReady=false; }
  }
  if(!footReady){
    x.textAlign='center';x.fillStyle=brandGradient(W/2-140,0,W/2+140,0);x.font='800 22px '+FF;
    x.fillText('deadgoodstickers.co.uk',W/2,H-42);
  }
  return c;
}

function exportPreview(){
  var art=getActive();if(!art)return;
  stopAnimation();
  var c=buildPreviewSheet();
  if(!c){startAnimation();return;}
  c.toBlob(function(blob){
    triggerDownload(blob,'deadgood-preview-'+baseName(art.name)+'.png');
    startAnimation();
  },'image/png');
}

/* PRINT PNG — clean, square, high-resolution production file. Border + background
   + artwork, clipped to the sticker shape, transparent elsewhere. NO branding,
   grid, cutline, text or shadow. Centred with padding; same visual as preview. */
function buildPrintCanvas(SZ){
  var art=getActive();if(!art)return null;
  var oc=document.createElement('canvas');oc.width=SZ;oc.height=SZ;
  var ox=oc.getContext('2d');

  var geo=getStickerGeometry();
  if(!geo||!geo.innerPts||!geo.innerPts.length||!geo.outerPts||!geo.outerPts.length)return null;

  ox.clearRect(0,0,SZ,SZ);
  /* Centre the CANVAS_RES artwork in the square with guaranteed padding so the
     sticker is never cropped, even at the maximum border. */
  var draw=SZ*0.94,off=(SZ-draw)/2;
  ox.translate(off,off);
  ox.scale(draw/CANVAS_RES,draw/CANVAS_RES);

  /* 1. Border colour fill (only when a border is enabled) */
  if(S.borderMm>0){
    buildPath(geo.outerPts,ox);
    ox.fillStyle=S.borderColour;
    ox.fill();
  }
  /* 2. Background colour behind the artwork (inside the sticker shape) */
  if(S.fillColour&&S.fillColour!=='none'){
    buildPath(geo.innerPts,ox);
    ox.fillStyle=S.fillColour;
    ox.fill();
  }
  /* 3. The artwork, clipped to the inner shape, at its scale/fit/position */
  ox.save();
  buildPath(geo.innerPts,ox);
  ox.clip();
  ox.drawImage(art.img,geo.srcX,geo.srcY,geo.srcW,geo.srcH,geo.drawX,geo.drawY,geo.drawW,geo.drawH);
  ox.restore();
  return oc;
}

function exportPrint(){
  var art=getActive();if(!art)return;
  var oc=buildPrintCanvas(1800);     /* high-res square export */
  if(!oc)return;
  oc.toBlob(function(blob){triggerDownload(blob,'deadgood-print-file-'+baseName(art.name)+'.png');},'image/png');
}

function triggerDownload(blob,filename){
  var url=URL.createObjectURL(blob);
  var a=document.createElement('a');a.href=url;a.download=filename;
  document.body.appendChild(a);a.click();document.body.removeChild(a);
  /* Revoking in the SAME synchronous turn as the click can cancel the transfer
     in WebKit before it has taken ownership of the blob — the download then
     fails silently or lands as a zero-byte file. Deferring the revoke costs
     nothing and removes the race; 10s is far longer than any browser needs to
     begin a local blob download, while still releasing the memory promptly.
     Download behaviour, filename, PNG generation and export sizes are all
     untouched — only WHEN the temporary URL is released changes. */
  setTimeout(function(){try{URL.revokeObjectURL(url);}catch(e){}},10000);
}

/* ═══════════════════════════════════════════════════════════════════
   UTILITY
═══════════════════════════════════════════════════════════════════ */
function escHtml(s){return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');}

function fullRefresh(){
  computeDimensions();
  applyBorderConstraints();
  commitActive();
  refreshBadges();
  refreshSummary();
  /* Patches ONLY the active card's DPI text — deliberately not refreshSlots(),
     which would rebuild the whole strip on every size/custom-size input event. */
  refreshActiveCardDpi();
  /* animation loop will handle render */
}

/* ═══════════════════════════════════════════════════════════════════
   EVENT WIRING
═══════════════════════════════════════════════════════════════════ */

/* file input */
fileInput.addEventListener('change',function(){loadFiles(fileInput.files);fileInput.value='';});

/* upload overlay click */
uploadOverlay.addEventListener('click',function(){fileInput.click();});

/* stage drag-drop */
stage.addEventListener('dragover',function(e){e.preventDefault();stage.style.outline='2px dashed rgba(255,216,77,.5)';});
stage.addEventListener('dragleave',function(){stage.style.outline='';});
stage.addEventListener('drop',function(e){
  e.preventDefault();stage.style.outline='';
  if(e.dataTransfer&&e.dataTransfer.files.length)loadFiles(e.dataTransfer.files);
});

/* shape chips */
root.querySelectorAll('[data-dgs-shape]').forEach(function(btn){
  btn.addEventListener('click',function(){
    S.shape=btn.getAttribute('data-dgs-shape');
    root.querySelectorAll('[data-dgs-shape]').forEach(function(b){b.classList.remove('active');});
    btn.classList.add('active');
    /* Entering a fixed-ratio shape (custom/circle/square) while on Custom size:
       seed the locked longest edge from the current size so the value doesn't
       jump, then swap the sub-block. (Rectangle uses its own W × H.) */
    if(S.sizePreset==='custom'&&S.shape!=='rectangle')S.customEdge=clampCustomEdge(S.longestEdgeMm||S.customEdge||75);
    refreshCustomSizeUI();
    fullRefresh();
  });
});

/* size chips */
root.querySelectorAll('[data-dgs-size]').forEach(function(btn){
  btn.addEventListener('click',function(){
    S.sizePreset=btn.getAttribute('data-dgs-size');
    root.querySelectorAll('[data-dgs-size]').forEach(function(b){b.classList.remove('active');});
    btn.classList.add('active');
    /* Seed the fixed-ratio locked longest edge from the current size so it starts
       at the sticker's present longest edge (e.g. 100mm), not a stale value. */
    if(S.sizePreset==='custom')S.customEdge=clampCustomEdge(S.longestEdgeMm||S.customEdge||75);
    refreshCustomSizeUI();
    fullRefresh();
  });
});

/* custom size inputs — independent W × H (Rectangle/Circle/Square custom size, unchanged) */
if(customW)customW.addEventListener('input',function(){var v=parseInt(customW.value,10);if(v>100){v=100;customW.value='100';}S.customW=v||75;fullRefresh();});
if(customH)customH.addEventListener('input',function(){var v=parseInt(customH.value,10);if(v>100){v=100;customH.value='100';}S.customH=v||75;fullRefresh();});
/* custom size LONGEST EDGE — fixed-ratio shapes (Custom/Circle/Square); aspect
   locked, 40–100mm. The finished dims are derived in computeDimensions(), which
   also re-clamps, so the badge/summary stay in range while typing; the low bound
   is enforced on blur so passing through e.g. "4" → "40" still types naturally. */
if(customEdge){
  customEdge.addEventListener('input',function(){
    var v=parseInt(customEdge.value,10);
    if(v>100){v=100;customEdge.value='100';}
    S.customEdge=isNaN(v)?75:v;
    fullRefresh();
  });
  customEdge.addEventListener('change',function(){
    var v=clampCustomEdge(parseInt(customEdge.value,10));
    customEdge.value=String(v);S.customEdge=v;
    fullRefresh();
  });
}

/* border slider */
borderSlider.addEventListener('input',function(){
  S.borderMm=parseFloat(borderSlider.value);
  if(S.shape==='custom'&&S.borderMm<2){S.borderMm=2;borderSlider.value='2';}
  borderVal.textContent=S.borderMm+'mm';
  /* sync border preset chips */
  root.querySelectorAll('[data-dgs-border-preset]').forEach(function(b){
    b.classList.toggle('active',parseFloat(b.getAttribute('data-dgs-border-preset'))===S.borderMm);
  });
  commitActive();
  refreshSummary();
  /* border width feeds effective DPI (see setColour) — keep the pill and the
     artwork card in step with the summary that was just repainted */
  refreshBadges();
  refreshActiveCardDpi();
});

/* Slider progress fill — ONE delegated listener covers dragging on all three
   sliders. No per-slider handler is added and none of the existing input
   handlers is modified; because they are bound on the element and this listens
   on the bubbling phase at root, any clamp they apply (e.g. the 2mm custom-shape
   floor) is already reflected in .value by the time this reads it. */
root.addEventListener('input',function(e){
  if(e.target&&e.target.classList&&e.target.classList.contains('dgs-slider'))dgsSyncSliderFill();
});

/* border preset chips */
root.querySelectorAll('[data-dgs-border-preset]').forEach(function(btn){
  btn.addEventListener('click',function(){
    S.borderMm=parseFloat(btn.getAttribute('data-dgs-border-preset'));
    borderSlider.value=String(S.borderMm);
    borderVal.textContent=S.borderMm+'mm';
    root.querySelectorAll('[data-dgs-border-preset]').forEach(function(b){b.classList.remove('active');});
    btn.classList.add('active');
    commitActive();
    refreshSummary();
    /* border width feeds effective DPI (see setColour) */
    refreshBadges();
    refreshActiveCardDpi();
    dgsSyncSliderFill();   /* preset wrote borderSlider.value directly */
  });
});

/* Corner Roundness slider (Square/Rectangle only — read live by the geometry) */
smoothSlider.addEventListener('input',function(){
  S.cornerRadius=parseInt(smoothSlider.value,10);
  smoothVal.textContent=S.cornerRadius+'mm';
  root.querySelectorAll('[data-dgs-smooth-preset]').forEach(function(b){
    b.classList.toggle('active',parseInt(b.getAttribute('data-dgs-smooth-preset'),10)===S.cornerRadius);
  });
  commitActive();
});

/* Corner Roundness preset chips */
root.querySelectorAll('[data-dgs-smooth-preset]').forEach(function(btn){
  btn.addEventListener('click',function(){
    S.cornerRadius=parseInt(btn.getAttribute('data-dgs-smooth-preset'),10);
    smoothSlider.value=String(S.cornerRadius);
    smoothVal.textContent=S.cornerRadius+'mm';
    root.querySelectorAll('[data-dgs-smooth-preset]').forEach(function(b){b.classList.remove('active');});
    btn.classList.add('active');
    commitActive();
    dgsSyncSliderFill();   /* preset wrote smoothSlider.value directly */
  });
});

/* border colour chips */
root.querySelectorAll('[data-dgs-bordercol]').forEach(function(btn){
  btn.addEventListener('click',function(){chooseBorderMode(btn.getAttribute('data-dgs-bordercol'));});
});
/* border colour custom picker */
if(borderPicker)borderPicker.addEventListener('input',function(){setColour(borderPicker.value);});
/* border hex — editable + paste, validated */
if(borderHexInput)borderHexInput.addEventListener('input',function(){
  var h=normHex(borderHexInput.value);
  borderHexInput.classList.toggle('invalid',!h);
  if(h){
    if(S.borderMm===0){S.borderMm=(S.shape==='custom')?2:2.5;borderSlider.value=String(S.borderMm);borderVal.textContent=S.borderMm+'mm';}
    setColour(h);
  }
});

/* background colour chips */
root.querySelectorAll('[data-dgs-fill]').forEach(function(btn){
  btn.addEventListener('click',function(){chooseFillMode(btn.getAttribute('data-dgs-fill'));});
});
/* background colour custom picker */
if(fillPicker)fillPicker.addEventListener('input',function(){setFill(fillPicker.value);});
/* background hex — editable + paste, validated */
if(fillHexInput)fillHexInput.addEventListener('input',function(){
  var h=normHex(fillHexInput.value);
  fillHexInput.classList.toggle('invalid',!h);
  if(h)setFill(h);
});

/* scale slider */
scaleSlider.addEventListener('input',function(){
  S.artScale=parseInt(scaleSlider.value,10)/100;
  scaleVal.textContent=scaleSlider.value+'%';
  commitActive();
  /* Effective DPI now depends on artScale, so the three surfaces that display
     it have to be re-read here — previously they could not go stale, because
     the old figure was derived from the uploaded file alone and artScale did
     not enter into it. Deliberately NOT fullRefresh(): dimensions and the
     border constraints are unaffected by scale, so only the DPI readouts are
     repainted. refreshActiveCardDpi() already patches a single text node and
     writes nothing when the value is unchanged. */
  refreshBadges();
  refreshSummary();
  refreshActiveCardDpi();
});

/* artwork control buttons */
root.querySelectorAll('[data-dgs-action]').forEach(function(btn){
  btn.addEventListener('click',function(){
    var action=btn.getAttribute('data-dgs-action');
    if(action==='reset'||action==='fit'||action==='centre'){
      S.artScale=1.0;scaleSlider.value='100';scaleVal.textContent='100%';
      commitActive();
    }
  });
});

/* zoom buttons */
root.querySelectorAll('[data-dgs-zoom]').forEach(function(btn){
  btn.addEventListener('click',function(){
    var z=btn.getAttribute('data-dgs-zoom');
    if(z==='fit'){resetView();return;}
    if(z==='+')displayZoom=Math.min(3,displayZoom+0.25);
    else if(z==='-')displayZoom=Math.max(0.5,displayZoom-0.25);
    if(displayZoom<=1.001){panX=0;panY=0;}   /* recentre when back to fit */
    zoomLabel.textContent=Math.round(displayZoom*100)+'%';
    applyCanvasTransform();
  });
});

/* preview pan — drag to inspect when zoomed in (viewer only) */
canvasEl.addEventListener('pointerdown',function(e){
  if(displayZoom<=1.001||!getActive())return;
  panning=true;panStartX=e.clientX-panX;panStartY=e.clientY-panY;
  try{canvasEl.setPointerCapture(e.pointerId);}catch(err){}
  applyCanvasTransform();e.preventDefault();
});
canvasEl.addEventListener('pointermove',function(e){
  if(!panning)return;
  panX=e.clientX-panStartX;panY=e.clientY-panStartY;
  applyCanvasTransform();
});
function endPan(){if(panning){panning=false;applyCanvasTransform();}}
canvasEl.addEventListener('pointerup',endPan);
canvasEl.addEventListener('pointercancel',endPan);
canvasEl.addEventListener('dblclick',function(){resetView();});

/* ── Premium tilt interaction (live preview only) ───────────────────────────
   Grab the sticker and gently tilt it in 3D (±7°) to catch the
   light — Apple-product feel, not a 3D viewer. Pure CSS transform on the canvas,
   composed with zoom/pan in applyCanvasTransform(): render(), geometry, the gloss
   + shadow drawing and ALL exports are untouched (gloss + shadow are baked into the
   canvas bitmap, so they rotate with it; drawImage/toDataURL read the flat bitmap).
   Active ONLY at 100%/Fit; disabled when zoomed (pan owns the canvas) and under
   prefers-reduced-motion. requestAnimationFrame drives a damped follow + spring-back
   and stops when idle (no per-frame work at rest). Touch uses the same pointer path. */
function tiltPtInPoly(px,py,poly){
  var inside=false,n=poly.length;
  for(var i=0,j=n-1;i<n;j=i++){
    var xi=poly[i].x,yi=poly[i].y,xj=poly[j].x,yj=poly[j].y;
    if(((yi>py)!==(yj>py))&&(px<(xj-xi)*(py-yi)/((yj-yi)||1e-6)+xi))inside=!inside;
  }
  return inside;
}
/* exact silhouette hit-test via the cached outer cut contour — no canvas readback */
function tiltOverSticker(e){
  var art=getActive();if(!art)return false;
  var geo=getStickerGeometry();if(!geo||!geo.outerPts)return false;
  var r=canvasEl.getBoundingClientRect();if(r.width<1||r.height<1)return false;
  var px=(e.clientX-r.left)/r.width*CANVAS_RES, py=(e.clientY-r.top)/r.height*CANVAS_RES;
  var op=geo.outerPts;
  if(op.length&&op[0]&&typeof op[0].x==='number')return tiltPtInPoly(px,py,op);   /* single contour (geometric) */
  for(var k=0;k<op.length;k++){if(op[k]&&op[k].length>2&&tiltPtInPoly(px,py,op[k]))return true;}  /* multi (custom) */
  return false;
}
function tiltLoop(){
  var alive=false;
  if(tiltOn){
    tiltRX+=(tiltTX-tiltRX)*TILT_DAMP;tiltRY+=(tiltTY-tiltRY)*TILT_DAMP;
    if(Math.abs(tiltTX-tiltRX)>0.01||Math.abs(tiltTY-tiltRY)>0.01)alive=true;
  } else if(tiltReleaseAt){
    var t=(Date.now()-tiltReleaseAt)/TILT_RELEASE_MS;
    if(t>=1){tiltRX=0;tiltRY=0;tiltReleaseAt=0;}
    else{var ez=1-Math.pow(1-t,3);tiltRX=tiltFromX*(1-ez);tiltRY=tiltFromY*(1-ez);alive=true;}
  }
  applyCanvasTransform();
  tiltRAF=alive?requestAnimationFrame(tiltLoop):null;
}
function tiltStartLoop(){if(!tiltRAF)tiltRAF=requestAnimationFrame(tiltLoop);}
canvasEl.addEventListener('pointerdown',function(e){
  if(!tiltAvailable())return;          /* zoom>1 → pan owns it; no art / reduced-motion → off */
  if(!tiltOverSticker(e))return;       /* grab the sticker, never the empty grid */
  tiltOn=true;tiltReleaseAt=0;
  tiltStartX=e.clientX;tiltStartY=e.clientY;tiltTX=tiltRX;tiltTY=tiltRY;
  try{canvasEl.setPointerCapture(e.pointerId);}catch(err){}
  updateCanvasCursor();tiltStartLoop();e.preventDefault();
},{passive:false});
canvasEl.addEventListener('pointermove',function(e){
  if(tiltOn){
    var dx=e.clientX-tiltStartX,dy=e.clientY-tiltStartY,k=TILT_MAX/TILT_SENS;
    tiltTY=Math.max(-TILT_MAX,Math.min(TILT_MAX,dx*k));    /* drag X → rotateY */
    tiltTX=Math.max(-TILT_MAX,Math.min(TILT_MAX,-dy*k));   /* drag Y → rotateX (inverted) */
    tiltStartLoop();e.preventDefault();return;
  }
  if(displayZoom<=1.001){               /* hover: grab cursor only over the sticker (throttled, no readback) */
    var now=Date.now();if(now-tiltHoverGate<60)return;tiltHoverGate=now;
    var was=overSticker;overSticker=tiltAvailable()&&tiltOverSticker(e);
    if(was!==overSticker)updateCanvasCursor();
  }
},{passive:false});
function tiltEnd(e){
  if(!tiltOn)return;
  tiltOn=false;
  try{canvasEl.releasePointerCapture(e.pointerId);}catch(err){}
  tiltFromX=tiltRX;tiltFromY=tiltRY;tiltReleaseAt=Date.now();   /* smooth spring-back to flat */
  updateCanvasCursor();tiltStartLoop();
}
canvasEl.addEventListener('pointerup',tiltEnd);
canvasEl.addEventListener('pointercancel',tiltEnd);
canvasEl.addEventListener('pointerleave',function(){if(!tiltOn&&overSticker){overSticker=false;updateCanvasCursor();}});

/* download buttons — wrapped only to record what was downloaded this session
   (the export functions themselves are unchanged and reused as-is). */
/* Revision-stamped download tracking. The old dlPrint/dlPreview booleans
   LATCHED — once true they never went false again, so a download taken before
   any edit still counted afterwards. Storing the revision each file was taken
   at instead means "downloaded" is only ever true for the CURRENT project:
   the moment projectRev moves, both stamps are automatically stale. This
   replaces the two booleans rather than running alongside them, so there is no
   parallel tracking to keep in sync; refreshDlStatus() derives the same two
   booleans locally and is otherwise unchanged. -1 = never downloaded. */
var projectRev=0, dlPrintRev=-1, dlPreviewRev=-1;
function doExportPrint(){ if(!getActive())return; exportPrint(); dlPrintRev=projectRev; refreshDlStatus(); dgsSyncLeaveGuard(); }
function doExportPreview(){ if(!getActive())return; exportPreview(); dlPreviewRev=projectRev; refreshDlStatus(); dgsSyncLeaveGuard(); }
root.querySelector('[data-dgs-dl="proof"]').addEventListener('click',doExportPreview);
root.querySelector('[data-dgs-dl="print"]').addEventListener('click',doExportPrint);

/* ── shared modal open/close (animated) ─────────────────────────────────
   Open clears any pending close + plays the entry animation. Close plays the
   exit animation via an .is-closing class, then sets [hidden] only AFTER the
   animation finishes (so it fades/scales out instead of snapping). Reduced
   motion closes instantly. */
var DGS_MODAL_MS = 240;
function dgsReduceMotion(){try{return window.matchMedia('(prefers-reduced-motion: reduce)').matches;}catch(e){return false;}}
function dgsOpenModal(el){
  if(!el)return;
  if(el._closeTimer){clearTimeout(el._closeTimer);el._closeTimer=null;}
  el.classList.remove('is-closing');
  el.removeAttribute('hidden');
}
function dgsCloseModal(el){
  if(!el||el.hasAttribute('hidden')||el.classList.contains('is-closing'))return;
  if(dgsReduceMotion()){el.setAttribute('hidden','');return;}
  el.classList.add('is-closing');
  el._closeTimer=setTimeout(function(){el._closeTimer=null;el.classList.remove('is-closing');el.setAttribute('hidden','');},DGS_MODAL_MS+20);
}

/* ── Go-to-Order confirmation modal ─────────────────────────────────────
   Intercepts the "Continue to Order" CTA and offers the downloads first.
   The customer can always continue; downloads are never forced. */
var orderModal     = root.querySelector('[data-dgs-modal]');
var modalDlPrint   = orderModal&&orderModal.querySelector('[data-dgs-modal-dl="print"]');
var modalDlPreview = orderModal&&orderModal.querySelector('[data-dgs-modal-dl="preview"]');
var modalDone      = orderModal&&orderModal.querySelector('[data-dgs-modal-done]');
function refreshDlStatus(){
  /* derived from the revision stamps — true only for the CURRENT project */
  var dlPrint=(dlPrintRev===projectRev), dlPreview=(dlPreviewRev===projectRev);
  if(modalDlPrint){modalDlPrint.textContent=dlPrint?'✓ Print PNG downloaded':'⬇ Download Print PNG';modalDlPrint.classList.toggle('done',dlPrint);}
  if(modalDlPreview){modalDlPreview.textContent=dlPreview?'✓ Preview Image downloaded':'⬇ Download Preview Image';modalDlPreview.classList.toggle('done',dlPreview);}
  if(modalDone){if(dlPrint&&dlPreview)modalDone.removeAttribute('hidden');else modalDone.setAttribute('hidden','');}
}
function openOrderModal(){
  if(!orderModal)return;
  var hasArt=!!getActive();
  if(modalDlPrint)modalDlPrint.disabled=!hasArt;
  if(modalDlPreview)modalDlPreview.disabled=!hasArt;
  refreshDlStatus();
  dgsOpenModal(orderModal);
}
function closeOrderModal(){ dgsCloseModal(orderModal); }
if(orderModal){
  /* intercept the main CTA */
  var goOrder=root.querySelector('[data-dgs-go-order]');
  if(goOrder)goOrder.addEventListener('click',function(e){e.preventDefault();openOrderModal();});
  /* keep the modal's "Continue" link in sync with the CTA destination */
  var modalContinue=orderModal.querySelector('[data-dgs-modal-continue]');
  if(modalContinue&&goOrder)modalContinue.setAttribute('href',goOrder.getAttribute('href')||'#');
  /* modal download buttons reuse the same wrappers */
  if(modalDlPrint)modalDlPrint.addEventListener('click',doExportPrint);
  if(modalDlPreview)modalDlPreview.addEventListener('click',doExportPreview);
  /* stay / dismiss */
  var stayBtn=orderModal.querySelector('[data-dgs-modal-stay]');
  if(stayBtn)stayBtn.addEventListener('click',closeOrderModal);
  var dismiss=orderModal.querySelector('[data-dgs-modal-dismiss]');
  if(dismiss)dismiss.addEventListener('click',closeOrderModal);
  /* continue link closes the modal (navigation then proceeds via the href) */
  if(modalContinue)modalContinue.addEventListener('click',closeOrderModal);
  /* Esc closes (stay) */
  document.addEventListener('keydown',function(e){if(e.key==='Escape'&&!orderModal.hasAttribute('hidden'))closeOrderModal();});
  /* portal to <body> so the full-viewport backdrop sits above ALL page content
     (incl. the Shopify header) regardless of any section/ancestor stacking context */
  try{document.body.appendChild(orderModal);}catch(e){}
}

/* ── Welcome modal (first-time on load + permanent Help button) ──────────
   Self-contained: shows once per browser session, or whenever Help is clicked.
   "Don't show this again" persists in localStorage and only stops the AUTO
   open on load — Help always works. */
var WELCOME_HIDE_KEY = 'dgsWelcomeHidden';   /* localStorage: '1' = never auto-show */
var WELCOME_SEEN_KEY = 'dgsWelcomeSeen';     /* sessionStorage: shown this session */
var welcome   = root.querySelector('[data-dgs-welcome]');
var helpBtn   = root.querySelector('[data-dgs-help]');
if(welcome){
  var wmDontShow = welcome.querySelector('[data-dgs-welcome-dontshow]');
  var lsGet=function(k){try{return localStorage.getItem(k);}catch(e){return null;}};
  var lsSet=function(k,v){try{localStorage.setItem(k,v);}catch(e){}};
  var lsDel=function(k){try{localStorage.removeItem(k);}catch(e){}};
  function openWelcome(){
    if(wmDontShow)wmDontShow.checked=(lsGet(WELCOME_HIDE_KEY)==='1');
    dgsOpenModal(welcome);
  }
  function closeWelcome(){dgsCloseModal(welcome);}
  /* checkbox persists immediately */
  if(wmDontShow)wmDontShow.addEventListener('change',function(){
    if(wmDontShow.checked)lsSet(WELCOME_HIDE_KEY,'1');else lsDel(WELCOME_HIDE_KEY);
  });
  /* buttons / dismiss */
  var startBtn=welcome.querySelector('[data-dgs-welcome-start]');
  if(startBtn)startBtn.addEventListener('click',closeWelcome);
  var wmDismiss=welcome.querySelector('[data-dgs-welcome-dismiss]');
  if(wmDismiss)wmDismiss.addEventListener('click',closeWelcome);
  document.addEventListener('keydown',function(e){if(e.key==='Escape'&&!welcome.hasAttribute('hidden'))closeWelcome();});
  /* Help button always reopens it */
  if(helpBtn)helpBtn.addEventListener('click',openWelcome);
  /* portal to <body> so the full-viewport backdrop sits above ALL page content */
  try{document.body.appendChild(welcome);}catch(e){}
  /* auto-show once per session unless permanently hidden */
  var hidden=lsGet(WELCOME_HIDE_KEY)==='1',seen=false;
  try{seen=sessionStorage.getItem(WELCOME_SEEN_KEY)==='1';}catch(e){}
  if(!hidden&&!seen)openWelcome();
  try{sessionStorage.setItem(WELCOME_SEEN_KEY,'1');}catch(e){}
}

/* ── Floating control tooltips ────────────────────────────────────────────
   Presentation only. Reads the text refreshHelpTips() already writes into the
   hidden .dgs-tip elements and mirrors it into ONE shared fixed-position
   bubble beside the hovered or focused control. Touches no state, no
   validation, no availability, no defaults and nothing a control does.

   Why JS at all, rather than pure CSS: a CSS-only tooltip can only be
   positioned relative to an ancestor, and every candidate ancestor here sits
   inside .dgs-controls-col's overflow-y:auto — so it would be clipped, and if
   it overflowed right or bottom it would lengthen that scrollbar, which is the
   exact layout shift this pass exists to remove. Escaping the clipper needs
   position:fixed, and fixed coordinates cannot be derived in CSS. This is the
   whole helper: one element, two placements, no framework, no dependencies.

   ANCHORS — Priority 1 only:
     Shape  the Custom chip ALONE, so Circle/Square/Rectangle stay silent.
     Border its whole .dgs-control-section, so the slider and the preset chips
            share one anchor and moving between them cannot flicker.
     Scale  its whole .dgs-control-section.
   Corner Roundness has an identical .dgs-slider-row and is deliberately
   absent, as before.

   TOUCH — the pointer handlers ignore anything that is not a real mouse, and a
   non-mouse pointerdown force-hides, so a tap can never strand a bubble open.
   Keyboard focus is unconditional and works on every device. focusin/focusout
   bubble, so focusing the slider inside a section triggers its section anchor.

   PLACEMENT — left of the anchor by default: the inspector is pinned to the
   right edge of the viewport so that side is never free, while the preview
   panel to its left is wide and empty. Falls back to below the anchor if the
   left gutter is too narrow, and is clamped to the viewport on both axes, so
   it cannot be cut off by an edge or by either clipping ancestor. */
function dgsTooltips(){
  var GAP=10, EDGE=8;

  var bubble=document.createElement('div');
  bubble.className='dgs-tt';
  /* aria-hidden: this is a visual duplicate. The accessible description stays
     the aria-describedby .dgs-tip, so screen readers are never told twice. */
  bubble.setAttribute('aria-hidden','true');
  root.appendChild(bubble);

  var anchorEl=null, srcEl=null;

  /* Input modality. A touch tap fires pointerdown and THEN focusin, so a
     pointerdown guard alone cannot stop the focus path from re-opening the
     bubble on every mobile slider tap. Track how the user last interacted
     instead: touch/pen suppresses the focus trigger, while Tab always clears
     the flag so a keyboard remains fully served on any device. */
  var touchInput=false;
  document.addEventListener('pointerdown',function(e){
    touchInput=(e.pointerType!=='mouse');
  },{capture:true,passive:true});
  document.addEventListener('keydown',function(e){
    if(e.key==='Tab')touchInput=false;
  },{capture:true,passive:true});

  function place(){
    if(!anchorEl)return;
    /* Reset before measuring: shrink-to-fit width is computed against the space
       remaining from `left`, so a stale left from the previous anchor would
       wrap the text narrower than max-width. */
    bubble.style.left='0px';bubble.style.top='0px';
    var a=anchorEl.getBoundingClientRect();
    var b=bubble.getBoundingClientRect();
    var left=a.left-b.width-GAP;
    var top=a.top+(a.height-b.height)/2;
    if(left<EDGE){left=a.left;top=a.bottom+GAP;}   /* no left gutter — go below */
    left=Math.max(EDGE,Math.min(left,window.innerWidth -b.width -EDGE));
    top =Math.max(EDGE,Math.min(top ,window.innerHeight-b.height-EDGE));
    bubble.style.left=Math.round(left)+'px';
    bubble.style.top =Math.round(top )+'px';
  }

  function show(anchor,src){
    var text=((src&&src.textContent)||'').trim();
    if(!text)return;
    anchorEl=anchor;srcEl=src;
    bubble.textContent=text;
    bubble.classList.add('is-open');
    place();                       /* after text + open, so the box is final */
  }
  function hide(anchor){
    if(anchor&&anchorEl!==anchor)return;   /* a newer anchor already took over */
    anchorEl=null;srcEl=null;
    bubble.classList.remove('is-open');
  }

  function bind(anchor,srcId){
    var src=root.querySelector('#'+srcId);
    if(!anchor||!src)return;
    anchor.addEventListener('pointerenter',function(e){
      if(e.pointerType!=='mouse')return;          /* hover is mouse-only */
      show(anchor,src);
    });
    anchor.addEventListener('pointerleave',function(){hide(anchor);});
    anchor.addEventListener('pointerdown',function(e){
      if(e.pointerType!=='mouse')hide(anchor);    /* tap never leaves it open */
    });
    anchor.addEventListener('focusin' ,function(){
      if(touchInput)return;                       /* tap-focus never opens it */
      show(anchor,src);
    });
    anchor.addEventListener('focusout',function(){hide(anchor);});
  }

  function sectionOf(sel){
    var el=root.querySelector(sel);
    return el?el.closest('.dgs-control-section'):null;
  }
  bind(root.querySelector('[data-dgs-shape="custom"]'),'dgs-tip-shape');
  bind(sectionOf('[data-dgs-border-slider]'),'dgs-tip-border');
  bind(sectionOf('[data-dgs-scale-slider]'),'dgs-tip-scale');

  /* Keep an open bubble honest if its source text is rewritten underneath it —
     e.g. clicking Custom while still hovering it swaps the shape guidance. */
  dgsTipSync=function(){ if(anchorEl&&srcEl)show(anchorEl,srcEl); };

  /* The bubble is fixed to the viewport, so anything that moves the anchor
     without a pointer event must reposition it. Scroll does NOT bubble, and
     there are three possible scrollers here (the page, .dgs, and
     .dgs-controls-col), so capture at the document catches all of them with one
     listener and no assumption about which is doing the scrolling. Both are
     passive and place() no-ops unless a bubble is actually open. */
  window.addEventListener('resize',place,{passive:true});
  document.addEventListener('scroll',place,{capture:true,passive:true});
}
dgsTooltips();

/* ── Artwork Reset/Remove confirmation modal ──────────────────────────────
   Reuses the existing .dgs-modal chrome/animation; only the title, body and
   confirm-button label change depending on which action was requested. The
   actual resetArtwork(id)/removeArtwork(id) calls are unchanged — they only
   run after the customer explicitly confirms. */
var confirmModal  = root.querySelector('[data-dgs-confirm]');
var confirmTitle  = confirmModal&&confirmModal.querySelector('[data-dgs-confirm-title]');
var confirmBody   = confirmModal&&confirmModal.querySelector('[data-dgs-confirm-body]');
var confirmOkBtn  = confirmModal&&confirmModal.querySelector('[data-dgs-confirm-ok]');
var confirmPending = null;   /* {id, action:'reset'|'remove'} while the modal is open */
var CONFIRM_COPY = {
  reset:{title:'Reset artwork?',body:'This will restore this artwork slot to its default settings. Your uploaded file will stay in the slot.',ok:'Reset Artwork'},
  remove:{title:'Remove artwork?',body:'This will remove the artwork from this slot. This action cannot be undone.',ok:'Remove Artwork'}
};
function openConfirm(action,id){
  if(!confirmModal)return;
  var copy=CONFIRM_COPY[action];if(!copy)return;
  confirmPending={id:id,action:action};
  if(confirmTitle)confirmTitle.textContent=copy.title;
  if(confirmBody)confirmBody.textContent=copy.body;
  if(confirmOkBtn)confirmOkBtn.textContent=copy.ok;
  dgsOpenModal(confirmModal);
}
function closeConfirm(){ confirmPending=null; dgsCloseModal(confirmModal); }
if(confirmModal){
  if(confirmOkBtn)confirmOkBtn.addEventListener('click',function(){
    var pending=confirmPending;
    closeConfirm();
    if(!pending)return;
    if(pending.action==='reset')resetArtwork(pending.id);
    else if(pending.action==='remove')removeArtwork(pending.id);
  });
  var confirmCancel=confirmModal.querySelector('[data-dgs-confirm-cancel]');
  if(confirmCancel)confirmCancel.addEventListener('click',closeConfirm);
  var confirmDismiss=confirmModal.querySelector('[data-dgs-confirm-dismiss]');
  if(confirmDismiss)confirmDismiss.addEventListener('click',closeConfirm);
  document.addEventListener('keydown',function(e){if(e.key==='Escape'&&!confirmModal.hasAttribute('hidden'))closeConfirm();});
  /* portal to <body> so the full-viewport backdrop sits above ALL page content
     (incl. the Shopify header), same as the other two modals */
  try{document.body.appendChild(confirmModal);}catch(e){}
}

/* One control — "View Current Summary" — expands/collapses ALL THREE download cards
   together (each reveals its own content INSIDE its own card): Preview thumbnail,
   Print thumbnail, Summary rows. Thumbnails are generated on expand by reusing
   render()/buildPrintCanvas() READ-ONLY; the summary rows stay live via
   refreshSummary(). Downloads/exports are unchanged. */
(function(){
  var tgl=root.querySelector('[data-dgs-summary-toggle]');
  var panels=root.querySelectorAll('[data-dgs-expand]');
  if(!tgl||!panels.length)return;
  var pWrap=root.querySelector('[data-dgs-ref-preview]');
  var pImg=root.querySelector('[data-dgs-sum-preview-thumb]');
  var kImg=root.querySelector('[data-dgs-sum-print-thumb]');
  function hideImg(im){if(im){im.removeAttribute('src');im.setAttribute('hidden','');}}
  function buildThumbs(){
    if(!getActive()){hideImg(pImg);hideImg(kImg);return;}
    /* sticker preview — square, on the SELECTED grid (NOT the branded proof sheet) */
    try{
      render(0,{sheen:false});                       /* clean static sticker on the live canvas */
      if(pImg){pImg.src=canvasEl.toDataURL('image/png');pImg.removeAttribute('hidden');}
      if(pWrap)pWrap.classList.toggle('is-light',!!gridLight);
    }catch(e){hideImg(pImg);}
    /* print-file preview — square, real transparency on the card (no checkerboard) */
    try{
      var pc=buildPrintCanvas(420);
      if(kImg&&pc){kImg.src=pc.toDataURL('image/png');kImg.removeAttribute('hidden');}else{hideImg(kImg);}
    }catch(e){hideImg(kImg);}
  }
  /* Let activateArtwork() repaint the thumbnails in place when the customer
     switches artwork with the panels already open. Guarded on the expanded
     state, so nothing is generated while collapsed and the open/closed state is
     never touched — this only refreshes two <img> src values. Deliberately NOT
     wired into fullRefresh(): that fires on every slider `input`, and
     buildThumbs() runs buildPrintCanvas() plus two toDataURL() calls. */
  dgsThumbSync=function(){
    if(!panels[0].hasAttribute('hidden'))buildThumbs();
  };

  tgl.addEventListener('click',function(){
    var open=panels[0].hasAttribute('hidden');          /* all panels share one state */
    if(open)buildThumbs();
    for(var i=0;i<panels.length;i++){ if(open)panels[i].removeAttribute('hidden'); else panels[i].setAttribute('hidden',''); }
    tgl.setAttribute('aria-expanded',open?'true':'false');
    tgl.textContent=open?'Hide Summary ▴':'View Current Summary ▾';
  });
})();

/* live preview grid light/dark toggle. Toggles a class on the stage-wrap (swaps the live
   grid surface in CSS) and mirrors the state into `gridLight` so the Preview Image proof
   panel matches what's on screen. Session-only (not persisted). Never affects the Print PNG. */
(function(){
  var gridBtn=root.querySelector('[data-dgs-gridtoggle]');
  var stageWrap=root.querySelector('.dgs-stage-wrap');
  if(!gridBtn||!stageWrap)return;
  gridBtn.addEventListener('click',function(){
    var on=stageWrap.classList.toggle('dgs-grid-light');
    gridLight=on;                                   /* mirror into the Preview Image export */
    gridBtn.setAttribute('aria-pressed',on?'true':'false');
  });
})();

/* ═══════════════════════════════════════════════════════════════════
   UNSAVED PROJECT / LEAVE PROTECTION

   The studio never saves online, so leaving loses the work. This guards that
   without inconveniencing anyone who has not started.

   THE MODEL — one counter, two stamps, no pile of booleans:
     projectRev    increments on every real project change
     dlPrintRev    the revision the Print PNG was last taken at
     dlPreviewRev  the revision the Preview Image was last taken at
   A project is SAFE only while BOTH stamps equal the current revision, so any
   edit makes earlier downloads stale by construction — there is no flag to
   forget to clear.

   WHERE THE REVISION MOVES — deliberately at existing choke points, not on new
   per-control listeners: commitActive() (all 12 setting call sites), the
   artwork push in loadFiles(), activateArtwork(), removeArtwork() and
   resetArtwork(). Opening or closing a modal, the welcome/Help dialog and the
   two download buttons all leave the revision alone, so none of them can
   produce a false unsaved state.

   TWO LAYERS: a branded modal for same-tab link clicks we can intercept, and
   the native beforeunload for refresh / tab close / typed URLs / browser
   Back — which cannot be intercepted cleanly. No pushState, no history trap.
═══════════════════════════════════════════════════════════════════ */
var leaveModal   = root.querySelector('[data-dgs-leave]');
var leaveTitleEl = leaveModal&&leaveModal.querySelector('[data-dgs-leave-title]');
var leaveBodyEl  = leaveModal&&leaveModal.querySelector('[data-dgs-leave-body]');
var leaveStayBtn = leaveModal&&leaveModal.querySelector('[data-dgs-leave-stay]');
var leaveGoBtn   = leaveModal&&leaveModal.querySelector('[data-dgs-leave-go]');
var leavePendingUrl = null;      /* the exact href that was clicked */
var leaveBypass = false;         /* true only while ONE approved navigation runs */
var leaveBypassTimer = null;

var LEAVE_COPY = {
  general:{
    title:'Before you leave…',
    body:'Your Design Studio project isn’t saved online. Download your files before leaving if you want to keep your work.',
    stay:'DOWNLOAD FILES',
    go:'LEAVE ANYWAY'
  },
  order:{
    title:'Before you order…',
    body:'Download your Print PNG and Preview Image first. Your Design Studio project will be lost when you leave this page.',
    stay:'DOWNLOAD FILES',
    go:'CONTINUE TO ORDER →'
  }
};

function dgsHasProject(){ return S.artworks.length>0; }
function dgsProjectSaved(){ return dlPrintRev===projectRev && dlPreviewRev===projectRev; }
function dgsProjectUnsaved(){ return dgsHasProject() && !dgsProjectSaved(); }

/* Standard modern pattern. No custom text — every current browser ignores it
   and shows its own wording. */
function dgsBeforeUnload(e){ e.preventDefault(); e.returnValue=''; return ''; }
function dgsSyncLeaveGuard(){
  /* addEventListener de-duplicates an identical (type, listener) pair, so this
     is safe to call as often as we like. */
  if(dgsProjectUnsaved()&&!leaveBypass) window.addEventListener('beforeunload',dgsBeforeUnload);
  else window.removeEventListener('beforeunload',dgsBeforeUnload);
}
function dgsMarkProjectChanged(){
  projectRev++;
  /* Emptied back out: drop the stamps so a fresh upload cannot inherit a stale
     "already downloaded" state from the artwork that was just removed. */
  if(!dgsHasProject()){ dlPrintRev=-1; dlPreviewRev=-1; }
  dgsSyncLeaveGuard();
}

function dgsScrollToDownloads(){
  var sec=root.querySelector('.dgs-download-section');
  if(!sec)return;
  try{ sec.scrollIntoView({behavior:'smooth',block:'start'}); }
  catch(e){ try{ sec.scrollIntoView(); }catch(e2){} }
}

function dgsOpenLeaveModal(variant,url){
  if(!leaveModal)return false;
  var copy=LEAVE_COPY[variant]||LEAVE_COPY.general;
  leavePendingUrl=url;
  if(leaveTitleEl)leaveTitleEl.textContent=copy.title;
  if(leaveBodyEl) leaveBodyEl.textContent =copy.body;
  if(leaveStayBtn)leaveStayBtn.textContent=copy.stay;
  if(leaveGoBtn)  leaveGoBtn.textContent  =copy.go;
  dgsOpenModal(leaveModal);
  return true;
}
function dgsCloseLeaveModal(){ leavePendingUrl=null; dgsCloseModal(leaveModal); }

/* Proceed to the EXACT href that was clicked, bypassing both layers for that
   one navigation only. If the navigation never happens — blocked, cancelled,
   an unreachable host — the bypass expires and full protection returns, so a
   single approved click can never disarm the rest of the session. */
function dgsProceedToPending(){
  var url=leavePendingUrl;
  dgsCloseLeaveModal();
  if(!url)return;
  leaveBypass=true;
  dgsSyncLeaveGuard();
  if(leaveBypassTimer)clearTimeout(leaveBypassTimer);
  leaveBypassTimer=setTimeout(function(){ leaveBypass=false; dgsSyncLeaveGuard(); },2500);
  window.location.href=url;
}

/* A link that only changes the hash does not unload the document. */
function dgsIsSameDocument(a){
  try{
    var u=new URL(a.href,window.location.href);
    return u.origin===window.location.origin &&
           u.pathname===window.location.pathname &&
           u.search===window.location.search;
  }catch(e){ return false; }
}

/* Delegated on DOCUMENT, not on [data-dgs]: the order/deals CTA is a separate
   section outside the studio's DOM, as are the header, menu and footer links
   this is meant to protect. Bubble phase and an early bail-out on every
   non-navigating case, so nothing else on the page is affected. */
document.addEventListener('click',function(e){
  if(!leaveModal)return;
  if(e.defaultPrevented)return;
  if(e.button!==0)return;                                   /* middle / right click */
  if(e.metaKey||e.ctrlKey||e.shiftKey||e.altKey)return;     /* new tab / window / save */
  if(!dgsProjectUnsaved())return;                           /* nothing to protect */
  var a=(e.target&&e.target.closest)?e.target.closest('a[href]'):null;
  if(!a)return;                                             /* buttons, controls, canvas */
  if(leaveModal.contains(a))return;                         /* never intercept our own modal */
  if(a.hasAttribute('download'))return;                     /* a download does not unload */
  var t=(a.getAttribute('target')||'').toLowerCase();
  if(t&&t!=='_self')return;                                 /* _blank keeps this page open */
  var raw=(a.getAttribute('href')||'').trim();
  if(!raw||raw==='#'||raw.charAt(0)==='#')return;           /* empty / same-page anchors */
  if(/^(javascript:|mailto:|tel:|sms:)/i.test(raw))return;  /* never navigates away */
  if(dgsIsSameDocument(a))return;                           /* hash-only on this page */
  /* ORDER CUSTOM STICKERS gets the conversion-friendly wording; everything
     else, including VIEW STICKER DEALS, gets the general warning. Matched on
     the CTA's own existing class, so that section needs no modification. */
  var variant=a.classList.contains('dgs-order-cta__button--primary')?'order':'general';
  if(dgsOpenLeaveModal(variant,a.href))e.preventDefault();
},false);

if(leaveModal){
  /* DOWNLOAD FILES (both variants): cancel the navigation, close, stay put and
     scroll to the existing Download Files section. Downloads are never
     triggered automatically and no studio state is touched. */
  if(leaveStayBtn)leaveStayBtn.addEventListener('click',function(){
    dgsCloseLeaveModal();
    dgsScrollToDownloads();
  });
  if(leaveGoBtn)leaveGoBtn.addEventListener('click',dgsProceedToPending);
  var leaveDismiss=leaveModal.querySelector('[data-dgs-leave-dismiss]');
  if(leaveDismiss)leaveDismiss.addEventListener('click',dgsCloseLeaveModal);
  document.addEventListener('keydown',function(e){
    if(e.key==='Escape'&&!leaveModal.hasAttribute('hidden'))dgsCloseLeaveModal();
  });
  /* portal to <body>, same as the other three modals */
  try{document.body.appendChild(leaveModal);}catch(e){}
}

/* ═══════════════════════════════════════════════════════════════════
   INIT
═══════════════════════════════════════════════════════════════════ */
applyBorderConstraints();
syncBorderColourUI();
syncFillUI();
refreshSlots();
refreshBadges();
refreshSummary();
render(0);

})();
